import {
  MAPS, MapConfig, mapById, RoomPhase, PeerInfo, ReportReason, moderateChat, MAX_CHAT,
  moderateTeamName, DEFAULT_TEAM_NAMES, MAX_TEAM_NAME, moderateName, MAX_NAME,
} from "@tak/sim";
import { MuteList } from "./lobby/mute";
import {
  DISC_ICONS, DiscIcon, Profile, loadProfile, saveProfile, isOwned, priceLabel, iconGlyph,
  canUseIcons, NONE,
} from "./lobby/profile";
import * as account from "./lobby/account";
import { getGuestId } from "./lobby/identity";
import { diagnosticsText } from "./obs/clientLog";
import { sendFeedback, type FeedbackKind } from "./lobby/feedback";
import { injectThemeTokens } from "./theme";
import {
  ColorFilter, isColorFilter, clampUiScale, DEFAULT_UI_SCALE, MIN_UI_SCALE, MAX_UI_SCALE,
  MIN_CONTRAST, MAX_CONTRAST, MIN_SATURATION, MAX_SATURATION,
  clampStrength, clampContrast, clampSaturation, applyUiScale, applyVision, TEAM_COLORS,
} from "./a11y";
import {
  KeyBindings, Action, ACTIONS, ACTION_LABELS, defaultBindings, sanitizeBindings,
  assignKey, keyLabel, normalizeKey,
} from "./controls/keybinds";

export interface Settings {
  volume: number;
  muted: boolean;
  zoom: number;
  mapId: string;
  goalLimit: number;
  timeLimit: number;
  /** Nombres de los equipos para el marcador. Online los fija el admin de la sala. */
  teamNames: [string, string];
  /** MOB-1 · Mando táctil: "auto" lo decide el dispositivo, "on"/"off" lo fuerzan. */
  touchControls: TouchMode;
  /** Mapa de teclas remapeable. */
  keybinds: KeyBindings;

  /* --- gráficos --- */
  /** Resolución de render: "auto" la baja sola si caen los fps. */
  quality: Quality;
  /** Partículas y estelas de patada. */
  effects: boolean;
  /**
   * Estelas detrás de los discos.
   *
   * Ajuste propio y no parte de `effects` porque responde a otra pregunta: los
   * efectos de patada son un destello puntual, la estela se dibuja SIEMPRE, en
   * cada frame y por cada jugador. Medido cuesta ~0,8 ms de los 16,6 con ocho
   * jugadores (ver bench/trails.html), que en un ordenador sobra — pero no hay
   * medición en móvil, y hasta que la haya esto tiene que poder apagarse solo.
   */
  trails: boolean;

  /* --- lo que se muestra del partido --- */
  showFps: boolean;
  showPing: boolean;
  /** Nombres flotando sobre los discos. */
  showNames: boolean;

  /* --- red --- */
  /** Cuánto colchón de interpolación se usa para dibujar a los rivales. */
  extrapolation: Extrapolation;

  /* --- accesibilidad --- */
  /** Corrección de color aplicada a TODA la imagen (no a colores sueltos). */
  colorFilter: ColorFilter;
  /** Intensidad de esa corrección (0..1). */
  filterStrength: number;
  contrast: number;
  saturation: number;
  /** Marca de forma por equipo (anillo lleno / partido) además del color. */
  teamMarkers: boolean;
  /** Suaviza los fogonazos de gol y los destellos a pantalla completa. */
  reduceFlashes: boolean;
  /** Multiplicador del tamaño de texto de la interfaz. */
  uiScale: number;
}

/** Modo del mando táctil (MOB-1). */
export type TouchMode = "auto" | "on" | "off";
/** Calidad de render: "auto" deja actuar al escalador adaptativo. */
export type Quality = "auto" | "high" | "low";
/** Colchón de interpolación de los rivales (ver `PredictiveClient.interpBias`). */
export type Extrapolation = "smooth" | "auto" | "sharp";

/** Sesgo en ticks que aplica cada modo al delay de interpolación. */
export const EXTRAPOLATION_BIAS: Record<Extrapolation, number> = {
  smooth: 3, auto: 0, sharp: -2,
};

const STORAGE_KEY = "disco-football-settings";
/**
 * Clave HISTÓRICA de la lista de jugadores local. Ya no se escribe: solo se
 * BORRA al arrancar.
 *
 * Una versión antigua guardaba aquí una plantilla de ejemplo (VIPER, PHOENIX,
 * JETT, SAGE) para enseñar el arrastre entre equipos, y esa plantilla se quedó
 * viviendo en el navegador de quien la tuviera: en cada partida en solitario
 * seguían apareciendo cuatro jugadores que no existen. Borrarla es lo único que
 * lo cura de verdad — mientras el dato siga en `localStorage`, cualquier lectura
 * futura vuelve a resucitarlo.
 */
const LEGACY_ROSTER_KEY = "disco-football-roster";

export function defaultSettings(): Settings {
  return {
    volume: 0.5, muted: false, zoom: 1, mapId: "classic", goalLimit: 3, timeLimit: 180,
    teamNames: [...DEFAULT_TEAM_NAMES] as [string, string],
    touchControls: "auto",
    keybinds: defaultBindings(),
    quality: "auto",
    effects: true,
    trails: true,
    showFps: true,
    showPing: true,
    showNames: true,
    extrapolation: "auto",
    colorFilter: "none",
    filterStrength: 1,
    contrast: 1,
    saturation: 1,
    teamMarkers: true,
    reduceFlashes: false,
    uiScale: DEFAULT_UI_SCALE,
  };
}

/** ¿Es uno de los valores de una lista cerrada? (lo de `localStorage` no es de fiar). */
function oneOf<T extends string>(v: unknown, values: readonly T[], def: T): T {
  return typeof v === "string" && (values as readonly string[]).includes(v) ? (v as T) : def;
}

const bool = (v: unknown, def: boolean): boolean => (typeof v === "boolean" ? v : def);

export function loadSettings(): Settings {
  const def = defaultSettings();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return def;
    const s = JSON.parse(raw) as Partial<Settings>;
    // Los nombres guardados vuelven a pasar por el filtro: el almacenamiento
    // local lo puede editar cualquiera a mano (SEC-2).
    const names = Array.isArray(s.teamNames) && s.teamNames.length === 2
      ? ([moderateTeamName(String(s.teamNames[0]), 0), moderateTeamName(String(s.teamNames[1]), 1)] as [string, string])
      : def.teamNames;
    return {
      volume: typeof s.volume === "number" ? s.volume : def.volume,
      muted: bool(s.muted, def.muted),
      zoom: typeof s.zoom === "number" ? s.zoom : def.zoom,
      mapId: typeof s.mapId === "string" ? s.mapId : def.mapId,
      goalLimit: typeof s.goalLimit === "number" ? s.goalLimit : def.goalLimit,
      timeLimit: typeof s.timeLimit === "number" ? s.timeLimit : def.timeLimit,
      teamNames: names,
      touchControls: oneOf<TouchMode>(s.touchControls, ["auto", "on", "off"], def.touchControls),
      // Lo guardado se vuelve a validar: `localStorage` lo edita cualquiera a
      // mano, y un mapa de teclas corrupto dejaría al jugador sin poder moverse.
      keybinds: sanitizeBindings(s.keybinds),
      quality: oneOf<Quality>(s.quality, ["auto", "high", "low"], def.quality),
      effects: bool(s.effects, def.effects),
      trails: bool(s.trails, def.trails),
      showFps: bool(s.showFps, def.showFps),
      showPing: bool(s.showPing, def.showPing),
      showNames: bool(s.showNames, def.showNames),
      extrapolation: oneOf<Extrapolation>(s.extrapolation, ["smooth", "auto", "sharp"], def.extrapolation),
      colorFilter: isColorFilter(s.colorFilter) ? s.colorFilter : def.colorFilter,
      filterStrength: clampStrength(s.filterStrength),
      contrast: clampContrast(s.contrast),
      saturation: clampSaturation(s.saturation),
      teamMarkers: bool(s.teamMarkers, def.teamMarkers),
      reduceFlashes: bool(s.reduceFlashes, def.reduceFlashes),
      uiScale: clampUiScale(s.uiScale),
    };
  } catch {
    return def;
  }
}

export function saveSettings(s: Settings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch {
    /* storage no disponible */
  }
}

export type Screen = "menu" | "playing";
export type Team = 0 | 1 | null;

export interface MatchSnapshot {
  score: [number, number];
  matchTime: number;
  timeLimit: number;
  goalLimit: number;
  mapId: string;
}

interface Roster {
  id: string;
  name: string;
  team: Team;
  local: boolean;
}

/**
 * Lista de jugadores de una partida LOCAL: solo tú.
 *
 * No se lee ni se guarda nada: quién hay en una partida es estado de ESA
 * partida, no una preferencia del dispositivo. Persistirlo es lo que hacía que
 * el navegador siguiera enseñando compañeros de una partida de hace semanas
 * (ver `LEGACY_ROSTER_KEY`). Online la lista la manda el servidor.
 */
function defaultRoster(): Roster[] {
  return [
    { id: "me", name: "TÚ", team: 0, local: true },
  ];
}

/** Tira la plantilla de ejemplo que dejó una versión antigua (una sola vez). */
function purgeLegacyRoster(): void {
  try { localStorage.removeItem(LEGACY_ROSTER_KEY); } catch { /* storage no disponible */ }
}

const fmtTime = (s: number): string => {
  const t = Math.max(0, Math.floor(s));
  const m = Math.floor(t / 60);
  const sec = t % 60;
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

/* ---- estilos del panel, inyectados una sola vez ---- */
const CSS = `
/* ============================================================================
   PANEL DE PARTIDA / MENÚ · piel "Tactical Broadcast".
   Aquí solo vive la MAQUETA de estas pantallas: el color, la tipografía, los
   biseles achaflanados y los estados de los controles los pone el sistema de
   diseño ("src/design/system.ts", clases .tb-*). Si algo de aquí redefine un
   color a mano, es un bug.
   ========================================================================== */

/* ------------------------------ overlay ---------------------------------- */
.vpm-root{position:fixed;inset:0;z-index:20;display:none;align-items:center;justify-content:center;
  padding:max(var(--sp-4),env(safe-area-inset-top)) max(var(--sp-4),env(safe-area-inset-right))
          max(var(--sp-4),env(safe-area-inset-bottom)) max(var(--sp-4),env(safe-area-inset-left));
  color:var(--tb-text);font-family:var(--tb-font-ui);
  background:radial-gradient(130% 110% at 50% -10%,rgba(18,28,40,0.82),rgba(4,6,10,0.96));
  backdrop-filter:blur(0.5rem);-webkit-backdrop-filter:blur(0.5rem);}
.vpm-root.open{display:flex;}
/* Rejilla táctica del fondo: se intuye, no se mira. */
.vpm-root::before{content:"";position:absolute;inset:0;pointer-events:none;opacity:.6;
  background-image:linear-gradient(rgba(34,230,255,0.05) 1px,transparent 1px),
    linear-gradient(90deg,rgba(34,230,255,0.05) 1px,transparent 1px);background-size:4rem 4rem;
  -webkit-mask-image:radial-gradient(120% 85% at 50% 0%,#000,transparent 78%);
  mask-image:radial-gradient(120% 85% at 50% 0%,#000,transparent 78%);}

/* ------------------------------- panel ----------------------------------- */
.vpm-panel{position:relative;width:min(72rem,100%);height:min(44rem,100%);
  display:flex;flex-direction:column;overflow:hidden;}
.vpm-head{flex:none;}
.vpm-title{font-family:var(--tb-font);font-style:italic;text-transform:uppercase;
  font-size:var(--fs-lg);letter-spacing:0.08em;font-weight:900;line-height:1;}
.vpm-title small{display:block;font-family:var(--tb-font-mono);font-style:normal;
  font-size:var(--fs-micro);letter-spacing:0.22em;color:var(--tb-muted);font-weight:600;margin-top:0.35rem;}

/* marcador del encabezado: simétrico como el del HUD */
.vpm-score{margin-left:auto;display:flex;align-items:center;gap:var(--sp-3);}
.vpm-scnum{font-family:var(--tb-font);font-style:italic;font-size:var(--fs-xl);font-weight:900;
  line-height:1;font-variant-numeric:tabular-nums;}
.vpm-scnum.red{color:var(--tb-magenta);text-shadow:var(--tb-glow-magenta);}
.vpm-scnum.blue{color:var(--tb-cyan);text-shadow:var(--tb-glow-cyan);}
.vpm-scteam{font-size:var(--fs-xs);letter-spacing:0.16em;font-weight:800;width:6rem;
  overflow:hidden;text-overflow:ellipsis;white-space:nowrap;text-transform:uppercase;}
.vpm-scteam.red{color:var(--tb-magenta);text-align:right;}
.vpm-scteam.blue{color:var(--tb-cyan);text-align:left;}
.vpm-clock{display:flex;flex-direction:column;align-items:center;padding:0 var(--sp-3);
  border-left:1px solid var(--tb-line);border-right:1px solid var(--tb-line);}
.vpm-clock b{font-family:var(--tb-font-mono);font-size:var(--fs-md);font-variant-numeric:tabular-nums;}
.vpm-clock span{font-size:var(--fs-micro);letter-spacing:0.2em;color:var(--tb-muted);}
@media (max-width:767.98px){.vpm-scteam{display:none;}}

/* invitación: botón discreto arriba a la derecha (NO enseña la URL) */
.vpm-copy{margin-left:auto;white-space:nowrap;}
/* cerrar: la esquina superior derecha del panel, como en cualquier ventana */
.vpm-close{flex:none;margin-left:var(--sp-2);width:2.4rem;height:2.4rem;padding:0;
  background:rgba(255,255,255,0.03);color:var(--tb-muted);cursor:pointer;
  font-size:var(--fs-sm);font-weight:800;line-height:1;
  clip-path:polygon(0.4rem 0,100% 0,100% calc(100% - 0.4rem),calc(100% - 0.4rem) 100%,0 100%,0 0.4rem);}
.vpm-copy + .vpm-close{margin-left:var(--sp-2);}
.vpm-head > .vpm-close:first-child, .vpm-title + .vpm-close{margin-left:auto;}
@media (hover:hover) and (pointer:fine){
  .vpm-close:hover{color:var(--tb-magenta);--edge:var(--tb-magenta);background:rgba(255,46,99,0.08);}
}
@media (pointer:coarse){.vpm-close{width:2.9rem;height:2.9rem;}}

/* ------------------------------- cuerpo ---------------------------------- */
.vpm-body{flex:1;display:flex;min-height:0;}
.vpm-content{flex:1;min-width:0;padding:var(--sp-5);display:flex;flex-direction:column;}
.vpm-content>*{flex:none;}

/* Columna de chat permanente en escritorio (densidad de información): la
   conversación de la sala se sigue leyendo mientras se toca cualquier otra
   pestaña. Debajo de 80rem no existe — el chat es su propia pestaña. */
.vpm-side{display:none;flex:0 0 20rem;border-left:1px solid var(--tb-line);
  background:rgba(0,0,0,0.25);padding:var(--sp-4);min-height:0;flex-direction:column;}
@media (min-width:80rem){.vpm-side{display:flex;}}

.vpm-h{font-size:var(--fs-xs);letter-spacing:0.24em;color:var(--tb-muted);font-weight:800;
  margin:0 0 var(--sp-3);text-transform:uppercase;display:flex;align-items:center;gap:var(--sp-3);}
.vpm-h::after{content:"";flex:1;height:1px;background:linear-gradient(90deg,var(--tb-line-2),transparent);}
.vpm-content .vpm-h:not(:first-child){margin-top:var(--sp-5);}
.vpm-hint{font-size:var(--fs-xs);color:var(--tb-muted);margin-top:var(--sp-3);line-height:1.55;letter-spacing:0.02em;}
.vpm-foot{flex:none;}
.vpm-foot .tb-btn{flex:1 1 8rem;}
/* Nota de estado del pie (lo que ve el invitado en lugar de los controles del
   anfitrión): informa sin fingir que hay algo que pulsar. */
.vpm-foot-note{flex:1 1 12rem;margin:0;align-self:center;font-size:var(--fs-xs);
  color:var(--tb-muted);letter-spacing:0.04em;line-height:1.5;}

/* ------------------------------- equipos --------------------------------- */
.vpm-teams{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:var(--sp-3);}
/* Las listas de jugadores son EL contenido de esta pantalla: con la sección de
   nombres fuera, el alto que sobra es suyo. */
.vpm-col{position:relative;display:flex;flex-direction:column;min-height:16rem;
  background:linear-gradient(158deg,rgba(19,27,36,0.75),rgba(8,12,18,0.75));
  transition:box-shadow .12s ease,background .12s ease;}
.vpm-col.drop{background:rgba(34,230,255,0.07);box-shadow:inset 0 0 0 2px var(--tb-cyan);}
.vpm-col-h{display:flex;align-items:center;justify-content:space-between;gap:var(--sp-2);
  padding:var(--sp-2) var(--sp-3);
  border-bottom:1px solid var(--tb-line);font-size:var(--fs-xs);letter-spacing:0.16em;font-weight:800;
  text-transform:uppercase;}
/* El NOMBRE del equipo se edita aquí mismo (es donde se lee). Parece texto
   hasta que se pulsa: sin caja ni relleno, para que la cabecera siga siendo una
   cabecera y no un formulario. El subrayado punteado es la única pista de que
   se puede tocar, y solo aparece si de verdad se puede. */
.vpm-col-name{flex:1;min-width:0;background:none;border:0;padding:0.15em 0;margin:0;color:inherit;
  font:inherit;font-size:var(--fs-xs);font-weight:800;letter-spacing:0.16em;text-transform:uppercase;
  overflow:hidden;text-overflow:ellipsis;white-space:nowrap;outline:none;
  border-bottom:1px dashed transparent;transition:border-color .12s ease;}
input.vpm-col-name{cursor:text;border-bottom-color:rgba(122,160,190,0.28);}
input.vpm-col-name:hover{border-bottom-color:currentColor;}
input.vpm-col-name:focus{border-bottom-color:currentColor;background:rgba(0,0,0,0.25);}
.vpm-col-name.ro{cursor:default;}
.vpm-col-n{flex:none;font-family:var(--tb-font-mono);font-variant-numeric:tabular-nums;opacity:.75;}
.vpm-col.red .vpm-col-h{color:var(--tb-magenta);background:linear-gradient(100deg,var(--tb-magenta-dim),transparent 70%);}
.vpm-col.blue .vpm-col-h{color:var(--tb-cyan);background:linear-gradient(100deg,var(--tb-cyan-dim),transparent 70%);}
.vpm-col.spec .vpm-col-h{color:var(--tb-muted);}
.vpm-col-list{flex:1;padding:var(--sp-2);display:flex;flex-direction:column;gap:var(--sp-2);}
.vpm-chip{cursor:grab;}
.vpm-chip:active{cursor:grabbing;}
.vpm-chip.dragging{opacity:.4;}
.vpm-chip.me{box-shadow:inset 0.22rem 0 0 var(--tb-cyan);}
.vpm-dot{width:0.55rem;height:0.55rem;flex:none;background:var(--tb-muted);
  clip-path:polygon(50% 0,100% 50%,50% 100%,0 50%);}
.vpm-col.red .vpm-dot{background:var(--tb-magenta);}
.vpm-col.blue .vpm-dot{background:var(--tb-cyan);}
.vpm-ping{margin-left:auto;font-family:var(--tb-font-mono);font-size:var(--fs-micro);
  font-variant-numeric:tabular-nums;letter-spacing:0.08em;}
/* Botones cuadrados de la ficha (expulsar, silenciar, reportar). El marco lo
   pinta .tb-bevel (pseudoelemento enmascarado) y no un border: un borde normal
   se lo come el recorte justo en las dos esquinas cortadas, que es donde se
   veía el hueco. */
.vpm-kick,.vpm-mod{width:1.7rem;height:1.7rem;flex:none;
  background:transparent;color:var(--tb-muted);font-size:var(--fs-micro);cursor:pointer;line-height:1;padding:0;
  clip-path:polygon(0.22rem 0,100% 0,100% calc(100% - 0.22rem),calc(100% - 0.22rem) 100%,0 100%,0 0.22rem);}
.vpm-mod{margin-left:var(--sp-1);}
.vpm-mod.on{--edge:var(--tb-magenta);color:var(--tb-magenta);}
@media (hover:hover) and (pointer:fine){
  .vpm-kick:hover{background:var(--tb-magenta);color:#05070b;--edge:var(--tb-magenta);}
  .vpm-mod:hover{--edge:var(--tb-warn);color:var(--tb-warn);}
}
@media (pointer:coarse){.vpm-kick,.vpm-mod{width:2.25rem;height:2.25rem;}}

/* ------------------------------ steppers --------------------------------- */
.vpm-row{display:flex;align-items:center;justify-content:space-between;gap:var(--sp-3);
  padding:var(--sp-3) 0;border-bottom:1px solid var(--tb-line);}
.vpm-row-l b{display:block;font-size:var(--fs-sm);letter-spacing:0.1em;text-transform:uppercase;}
.vpm-row-l span{font-size:var(--fs-xs);color:var(--tb-muted);letter-spacing:0.02em;}
.vpm-step{display:flex;align-items:center;gap:var(--sp-2);flex:none;}
.vpm-step button{width:2.5rem;height:2.5rem;background:var(--tb-panel-2);border:1px solid var(--tb-line-2);
  color:var(--tb-text);font-size:var(--fs-md);font-weight:900;cursor:pointer;
  transform:skewX(var(--tb-skew));transition:border-color .12s ease,color .12s ease;}
.vpm-step .val{min-width:4.5rem;text-align:center;font-family:var(--tb-font-mono);font-size:var(--fs-sm);
  font-weight:800;font-variant-numeric:tabular-nums;}
@media (hover:hover) and (pointer:fine){
  .vpm-step button:hover{border-color:var(--tb-cyan);color:var(--tb-cyan);}
}
@media (pointer:coarse){.vpm-step button{width:2.9rem;height:2.9rem;}}

/* -------------------------------- mapas ---------------------------------- */
.vpm-maps{display:grid;grid-template-columns:repeat(auto-fit,minmax(13rem,1fr));gap:var(--sp-3);}
.vpm-map{position:relative;padding:var(--sp-3);cursor:pointer;
  background:linear-gradient(158deg,rgba(19,27,36,0.8),rgba(8,12,18,0.8));
  transition:box-shadow .12s ease,transform .12s ease;}
.vpm-map b{font-family:var(--tb-font);font-style:italic;font-size:var(--fs-sm);letter-spacing:0.1em;}
.vpm-map span{font-size:var(--fs-xs);color:var(--tb-muted);}
.vpm-map.sel{box-shadow:inset 0 0 0 1px var(--tb-magenta),0 0 1.4rem rgba(255,46,99,0.22);}
.vpm-map.sel::after{content:"SELECCIONADO";position:absolute;top:var(--sp-2);right:var(--sp-3);
  font-family:var(--tb-font-mono);font-size:var(--fs-micro);letter-spacing:0.14em;color:var(--tb-magenta);font-weight:800;}
@media (hover:hover) and (pointer:fine){
  .vpm-map:hover{box-shadow:inset 0 0 0 1px var(--tb-cyan);transform:translateY(-2px);}
}
.vpm-map-hero{display:grid;grid-template-columns:minmax(0,1.6fr) minmax(0,1fr);gap:var(--sp-4);
  align-items:center;padding:var(--sp-4);margin-bottom:var(--sp-4);
  background:linear-gradient(158deg,rgba(19,27,36,0.8),rgba(8,12,18,0.8));}
.vpm-map-facts b{display:block;font-family:var(--tb-font);font-style:italic;font-size:var(--fs-md);
  letter-spacing:0.08em;margin-bottom:var(--sp-2);}
.vpm-map-facts span{display:block;font-size:var(--fs-xs);color:var(--tb-muted);line-height:1.6;}
.vpm-map-thumb{margin:var(--sp-2) 0;display:flex;align-items:center;justify-content:center;min-height:5.5rem;}
@media (max-width:767.98px){.vpm-map-hero{grid-template-columns:1fr;}}

/* ------------------------------ accesibilidad ---------------------------- */
.vpm-a11y-modes{display:flex;flex-direction:column;gap:var(--sp-2);margin:var(--sp-2) 0 var(--sp-4);}
.vpm-a11y-mode{display:flex;align-items:center;gap:var(--sp-3);min-height:2.9rem;padding:0.65em 0.9em;
  background:linear-gradient(160deg,var(--tb-panel-2),var(--tb-panel));border:1px solid var(--tb-line);
  color:var(--tb-text);cursor:pointer;text-align:left;font-size:var(--fs-xs);letter-spacing:0.12em;
  font-weight:800;text-transform:uppercase;transition:border-color .12s ease,box-shadow .12s ease;}
.vpm-a11y-mode.on{border-color:var(--tb-cyan);box-shadow:inset 0 0 0 1px var(--tb-cyan);}
.vpm-a11y-mode .sw{width:1.15rem;height:1.15rem;flex:0 0 auto;border:1px solid rgba(255,255,255,0.35);
  transform:skewX(var(--tb-skew));}
.vpm-a11y-mode .lb small{display:block;font-family:var(--tb-font-ui);font-size:var(--fs-micro);font-weight:600;
  letter-spacing:0.04em;color:var(--tb-muted);margin-top:0.15rem;text-transform:none;}
@media (hover:hover) and (pointer:fine){.vpm-a11y-mode:hover{border-color:var(--tb-cyan);}}
.vpm-slider-row{margin-bottom:var(--sp-2);}

/* --------------------------------- teclas -------------------------------- */
.vpm-keys{display:flex;flex-direction:column;gap:var(--sp-2);margin-top:var(--sp-2);}
.vpm-key-row{display:flex;align-items:center;gap:var(--sp-3);}
.vpm-key-row .k-name{flex:1;font-size:var(--fs-xs);color:var(--tb-muted);letter-spacing:0.1em;text-transform:uppercase;}
.vpm-key-row .k-btn{min-width:8rem;min-height:2.5rem;padding:0.5em 0.9em;background:var(--tb-panel-2);
  color:var(--tb-text);cursor:pointer;font-family:var(--tb-font-mono);
  font-weight:800;letter-spacing:0.08em;font-size:var(--fs-xs);
  clip-path:polygon(0.3rem 0,100% 0,100% calc(100% - 0.3rem),calc(100% - 0.3rem) 100%,0 100%,0 0.3rem);}
.vpm-key-row .k-btn.listening{--edge:var(--tb-magenta);color:var(--tb-magenta);}
@media (hover:hover) and (pointer:fine){.vpm-key-row .k-btn:hover{--edge:var(--tb-cyan);}}

/* --------------------------------- chat ---------------------------------- */
.vpm-chat{display:flex;flex-direction:column;height:100%;min-height:0;flex:1;}
.vpm-log{flex:1;min-height:8rem;overflow-y:auto;display:flex;flex-direction:column;gap:var(--sp-2);
  padding-right:var(--sp-2);}
.vpm-msg{font-size:var(--fs-xs);line-height:1.5;letter-spacing:0.02em;}
.vpm-msg .who{font-weight:800;}
.vpm-msg.sys{color:var(--tb-muted);font-style:italic;}
.vpm-msg .red{color:var(--tb-magenta);}
.vpm-msg .blue{color:var(--tb-cyan);}
.vpm-chatbar{display:flex;gap:var(--sp-2);margin-top:var(--sp-3);}
.vpm-chatbar input{flex:1;min-width:0;}

/* ------------------------------ menú principal --------------------------- */
.vpm-menu{position:relative;width:min(72rem,100%);display:grid;gap:var(--sp-5);
  grid-template-columns:1fr;justify-items:center;align-items:center;text-align:center;}
.vpm-menu-center{display:flex;flex-direction:column;align-items:center;gap:var(--sp-2);width:100%;}
.vpm-card{width:100%;}
.vpm-logo{font-family:var(--tb-font);font-style:italic;text-transform:uppercase;
  font-size:var(--fs-display);font-weight:900;letter-spacing:0.06em;line-height:0.95;
  color:var(--tb-text);text-shadow:0 0 2.5rem rgba(255,46,99,0.45),0 0 4rem rgba(34,230,255,0.25);}
.vpm-logo b{color:var(--tb-magenta);}
.vpm-tag{font-family:var(--tb-font-mono);font-size:var(--fs-xs);letter-spacing:0.5em;
  color:var(--tb-muted);margin-top:var(--sp-2);}
.vpm-menu-actions{display:flex;flex-direction:column;gap:var(--sp-3);width:min(22rem,100%);
  margin-top:var(--sp-5);}
.vpm-menu-actions .tb-btn{width:100%;}
.vpm-live{font-family:var(--tb-font-mono);font-size:var(--fs-micro);letter-spacing:0.2em;
  font-weight:700;color:var(--tb-muted);}
.vpm-live.on{color:var(--tb-cyan);text-shadow:var(--tb-glow-cyan);}
/* Tarjetas laterales del HUD de escritorio (identidad · estado). En móvil no
   existen: ahí la pantalla es para los tres botones que llevan a jugar. */
.vpm-menu-aside{display:none;flex-direction:column;gap:var(--sp-3);width:100%;text-align:left;}
.vpm-menu-aside .k{font-family:var(--tb-font-mono);font-size:var(--fs-micro);letter-spacing:0.18em;
  color:var(--tb-muted);text-transform:uppercase;padding-right:3.5rem;}
.vpm-menu-aside .v{font-size:var(--fs-sm);font-weight:800;letter-spacing:0.06em;margin-top:0.2rem;
  overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
@media (min-width:1024px){
  .vpm-menu{grid-template-columns:minmax(0,1fr) minmax(0,1.6fr) minmax(0,1fr);align-items:center;}
  .vpm-menu-aside{display:flex;}
  .vpm-menu-aside.right{text-align:right;}
}

/* ------------------------------- perfil ---------------------------------- */
/* Tu disco tal como sale a la cancha: el mismo color de equipo y el mismo icono.
   Es una vista previa, no una ilustración — si no coincidiera con lo que se ve
   jugando, no serviría para elegir. */
.vpm-prof-preview{display:flex;align-items:center;gap:var(--sp-4);margin-bottom:var(--sp-4);}
.vpm-prof-disc{width:4.5rem;height:4.5rem;flex:none;border-radius:50%;display:flex;
  align-items:center;justify-content:center;font-size:1.9rem;line-height:1;
  box-shadow:inset 0 0 0 2.5px rgba(0,0,0,0.9), 0 0 1.2rem rgba(0,0,0,0.6);}
.vpm-prof-disc.red{background:radial-gradient(circle at 38% 32%,#ff6b8f,var(--tb-magenta));}
.vpm-prof-disc.blue{background:radial-gradient(circle at 38% 32%,#7ef0ff,var(--tb-cyan));}
.vpm-prof-who{min-width:0;}
.vpm-prof-name{font-family:var(--tb-font);font-style:italic;text-transform:uppercase;
  font-size:var(--fs-lg);font-weight:900;letter-spacing:0.04em;overflow:hidden;text-overflow:ellipsis;}
.vpm-prof-tag{font-family:var(--tb-font-mono);font-size:var(--fs-micro);letter-spacing:0.2em;
  color:var(--tb-muted);font-weight:700;margin-top:0.2rem;}
.vpm-acc{margin:var(--sp-4) 0 0;}
.vpm-acc-row{display:flex;flex-direction:column;gap:0.25rem;padding:var(--sp-3);
  background:rgba(34,230,255,0.06);border-left:0.2rem solid var(--tb-cyan);}
.vpm-acc-nick{font-family:var(--tb-font-mono);font-size:var(--fs-sm);font-weight:800;
  letter-spacing:0.08em;color:var(--tb-text);}
.vpm-acc-meta{font-family:var(--tb-font-mono);font-size:var(--fs-micro);letter-spacing:0.12em;
  color:var(--tb-muted);text-transform:uppercase;}
.vpm-acc-btns{display:flex;gap:var(--sp-2);flex-wrap:wrap;margin:var(--sp-2) 0;}
/* Zona de borrado: separada y apagada. Lo irreversible no compite en peso
   visual con lo corriente — un botón rojo y grande al lado de "cerrar sesión"
   invita al accidente que se quiere evitar. */
.vpm-danger{margin:var(--sp-5) 0 0;padding:var(--sp-3);border:1px dashed var(--tb-line);
  display:flex;flex-direction:column;gap:var(--sp-2);align-items:flex-start;}
.vpm-danger .tb-btn.ghost{opacity:.75;}
.vpm-danger .vpm-prof-name-row{width:100%;}
.vpm-nick-note{min-height:1.2em;}
.vpm-prof-name-row{display:flex;gap:var(--sp-2);align-items:stretch;flex-wrap:wrap;}
.vpm-prof-name-row .tb-input{flex:1 1 12rem;}
.vpm-prof-name-row .tb-btn{flex:0 0 auto;}

.vpm-icons{display:grid;grid-template-columns:repeat(auto-fill,minmax(6.5rem,1fr));gap:var(--sp-2);}
.vpm-icon{display:flex;flex-direction:column;align-items:center;gap:0.25rem;padding:var(--sp-3) var(--sp-2);
  background:linear-gradient(160deg,var(--tb-panel-2),var(--tb-panel));color:var(--tb-text);
  cursor:pointer;font:inherit;}
.vpm-icon .g{font-size:1.5rem;line-height:1.2;}
.vpm-icon .n{font-size:var(--fs-xs);font-weight:700;letter-spacing:0.06em;}
.vpm-icon .p{font-family:var(--tb-font-mono);font-size:var(--fs-micro);letter-spacing:0.14em;
  color:var(--tb-muted);font-weight:800;}
.vpm-icon.on{--edge:var(--tb-cyan);}
.vpm-icon.on .p{color:var(--tb-cyan);}
/* De pago = ANUNCIADO, no a la venta. Se ve apagado y no responde: prometer un
   botón de compra que no cobra sería el señuelo más viejo del oficio. */
.vpm-icon.locked{opacity:.5;cursor:not-allowed;}
.vpm-icon.locked .p{color:var(--tb-warn);}

/* FEEDBACK. El bloque de diagnóstico se ENSEÑA (no se esconde): con scroll
   propio para que no empuje al botón de enviar fuera de la pantalla. */
.vpm-fb-kinds{display:flex;gap:var(--sp-2);flex-wrap:wrap;margin-bottom:var(--sp-3);}
.vpm-fb-text{width:100%;resize:vertical;min-height:8rem;font:inherit;line-height:1.45;}
.vpm-fb-tech{display:flex;align-items:center;gap:var(--sp-2);margin:var(--sp-3) 0 var(--sp-2);
  font-size:var(--fs-sm);color:var(--tb-muted);cursor:pointer;}
.vpm-fb-diag{max-height:11rem;overflow:auto;margin:0 0 var(--sp-2);padding:var(--sp-3);
  background:var(--tb-panel-2);border-left:2px solid var(--tb-cyan);
  font-family:var(--tb-font-mono);font-size:var(--fs-micro);line-height:1.5;
  color:var(--tb-muted);white-space:pre-wrap;word-break:break-word;}
@media (hover:hover) and (pointer:fine){
  .vpm-icon:not(.locked):hover{--edge:var(--tb-cyan);}
}

/* Tarjeta del menú que además es un botón (la de "Operador"). */
.vpm-card-btn{cursor:pointer;text-align:left;}
.vpm-card-cta{font-family:var(--tb-font-mono);font-size:var(--fs-micro);letter-spacing:0.14em;
  color:var(--tb-cyan);margin-top:0.4rem;opacity:.85;}
.vpm-op-icon{margin-right:0.35rem;}

/* ------------------------------- resultado ------------------------------- */
/* ======================= FINAL DE PARTIDO (celebración) ===================
   El resultado es el único momento del juego que se MIRA en vez de jugarse, y
   duraba un parpadeo: la fase pasaba a "over", el menú se cerraba solo y quien
   acababa de ganar veía su victoria medio segundo. Ahora entra con una
   secuencia de ~5 s (destello, ondas, el título aterrizando y el marcador
   contando) y NO se puede cerrar sola: solo la cierra quien juega.
   Todo con keyframes CSS — es la GPU la que compone; el hilo del juego ni se
   entera, que es lo que importa justo cuando acaba de terminar un partido. */
.vpm-result{text-align:center;justify-content:center;align-items:center;position:relative;overflow:hidden;}
/* Destello de apertura, del color del bando (o gris en empate). */
.vpm-res-flash{position:absolute;inset:0;pointer-events:none;z-index:0;opacity:0;
  animation:vpm-flash 1.1s ease-out forwards;}
.vpm-result.win .vpm-res-flash{background:radial-gradient(60% 55% at 50% 45%,rgba(34,230,255,0.55),transparent 70%);}
.vpm-result.lose .vpm-res-flash{background:radial-gradient(60% 55% at 50% 45%,rgba(255,46,99,0.5),transparent 70%);}
.vpm-result.draw .vpm-res-flash{background:radial-gradient(60% 55% at 50% 45%,rgba(160,180,196,0.35),transparent 70%);}
@keyframes vpm-flash{0%{opacity:0;}12%{opacity:1;}100%{opacity:0;}}
/* Ondas de choque: tres anillos que salen del centro, desfasados. */
.vpm-res-ring{position:absolute;left:50%;top:45%;width:14rem;height:14rem;margin:-7rem 0 0 -7rem;
  border:2px solid currentColor;border-radius:50%;opacity:0;pointer-events:none;z-index:0;
  animation:vpm-ring 2.4s cubic-bezier(.16,.7,.3,1) forwards;}
.vpm-res-ring:nth-of-type(2){animation-delay:.22s;}
.vpm-res-ring:nth-of-type(3){animation-delay:.44s;}
.vpm-result.win .vpm-res-ring{color:var(--tb-cyan);}
.vpm-result.lose .vpm-res-ring{color:var(--tb-magenta);}
.vpm-result.draw .vpm-res-ring{color:var(--tb-muted);}
@keyframes vpm-ring{0%{transform:scale(.2);opacity:.85;}100%{transform:scale(3.4);opacity:0;}}
/* Chispas: se reparten en abanico y caen. Son 12 divs, no un motor de
   partículas: cuesta cero y el navegador las compone en GPU. */
.vpm-res-spark{position:absolute;left:50%;top:48%;width:0.42rem;height:0.42rem;z-index:0;
  background:currentColor;opacity:0;pointer-events:none;
  animation:vpm-spark 2.6s cubic-bezier(.2,.7,.3,1) forwards;}
.vpm-result.win .vpm-res-spark{color:var(--tb-cyan);}
.vpm-result.lose .vpm-res-spark{color:var(--tb-magenta);}
.vpm-result.draw .vpm-res-spark{color:var(--tb-text-2);}
@keyframes vpm-spark{
  0%{opacity:0;transform:translate(0,0) scale(.4);}
  10%{opacity:1;}
  100%{opacity:0;transform:translate(var(--dx),var(--dy)) scale(1.1);}
}
/* El contenido entra por encima de los efectos. */
.vpm-result > *:not(.vpm-res-flash):not(.vpm-res-ring):not(.vpm-res-spark){position:relative;z-index:1;}
/* El título aterriza: entra grande y desenfocado y se clava. */
.vpm-result h1{animation:vpm-title .75s cubic-bezier(.14,.8,.24,1) both;}
@keyframes vpm-title{
  0%{opacity:0;transform:scale(2.1) translateY(-0.3em);filter:blur(0.5rem);letter-spacing:0.4em;}
  60%{opacity:1;filter:blur(0);}
  100%{opacity:1;transform:scale(1) translateY(0);letter-spacing:0.02em;}
}
/* …y late una vez colocado, como el letrero de un estadio. */
.vpm-result.win h1{animation:vpm-title .75s cubic-bezier(.14,.8,.24,1) both, vpm-pulse 1.6s 0.8s ease-in-out 2;}
@keyframes vpm-pulse{0%,100%{text-shadow:var(--tb-glow-cyan);}50%{text-shadow:0 0 2.2rem rgba(34,230,255,0.95),0 0 4rem rgba(34,230,255,0.6);}}
.vpm-result .vpm-res-sub{animation:vpm-fade .5s .1s both;}
.vpm-result .big{animation:vpm-pop .6s .45s cubic-bezier(.2,1.4,.3,1) both;}
.vpm-result .vpm-res-sides{animation:vpm-fade .5s .7s both;}
.vpm-result .vpm-res-stats{animation:vpm-fade .5s .95s both;}
@keyframes vpm-fade{from{opacity:0;transform:translateY(0.6rem);}to{opacity:1;transform:none;}}
@keyframes vpm-pop{from{opacity:0;transform:scale(.6);}to{opacity:1;transform:scale(1);}}
/* Los botones aparecen al final de la secuencia: si estuvieran desde el primer
   frame, medio mundo pulsaría REVANCHA antes de ver su propio marcador. */
.vpm-res-foot{animation:vpm-fade .5s 1.5s both;}
.vpm-result{text-align:center;justify-content:center;align-items:center;}
.vpm-result h1{font-family:var(--tb-font);font-style:italic;text-transform:uppercase;
  font-size:var(--fs-xl);letter-spacing:0.1em;font-weight:900;margin:0 0 var(--sp-2);}
.vpm-result h1.win{color:var(--tb-cyan);text-shadow:var(--tb-glow-cyan);}
.vpm-result h1.lose{color:var(--tb-magenta);text-shadow:var(--tb-glow-magenta);}
.vpm-result h1.draw{color:var(--tb-muted);}
.vpm-result .big{font-family:var(--tb-font);font-style:italic;font-size:var(--fs-display);
  font-weight:900;margin:var(--sp-3) 0;font-variant-numeric:tabular-nums;}
.vpm-result .big .red{color:var(--tb-magenta);}
.vpm-result .big .blue{color:var(--tb-cyan);}
.vpm-res-sub{font-family:var(--tb-font-mono);font-size:var(--fs-micro);letter-spacing:0.28em;
  color:var(--tb-muted);font-weight:700;}
.vpm-res-sides{display:flex;align-items:center;justify-content:center;gap:var(--sp-5);flex-wrap:wrap;}
.vpm-res-side{font-size:var(--fs-sm);letter-spacing:0.12em;font-weight:800;max-width:14rem;
  overflow:hidden;text-overflow:ellipsis;white-space:nowrap;text-transform:uppercase;}
.vpm-res-side.red{color:var(--tb-magenta);}
.vpm-res-side.blue{color:var(--tb-cyan);}
.vpm-res-players{margin-top:var(--sp-2);}
.vpm-res-players .vpm-res-side{font-size:var(--fs-xs);font-weight:600;color:var(--tb-muted);letter-spacing:0.06em;}
.vpm-res-stats{display:flex;justify-content:center;gap:var(--sp-5);margin:var(--sp-5) 0 var(--sp-2);flex-wrap:wrap;}
.vpm-res-stat{display:flex;flex-direction:column;gap:0.2rem;min-width:5rem;}
.vpm-res-stat b{font-family:var(--tb-font-mono);font-size:var(--fs-md);font-weight:900;font-variant-numeric:tabular-nums;}
.vpm-res-stat span{font-family:var(--tb-font-mono);font-size:var(--fs-micro);letter-spacing:0.18em;
  color:var(--tb-muted);font-weight:700;}

/* ---------------------- chat sobre la cancha (in-game) ------------------- */
.vpm-ig{position:fixed;left:max(var(--sp-3),env(safe-area-inset-left));
  bottom:max(var(--sp-3),env(safe-area-inset-bottom));z-index:14;width:min(24rem,44vw);
  display:flex;flex-direction:column;gap:var(--sp-1);pointer-events:none;font-family:var(--tb-font-ui);}
.vpm-ig-log{display:flex;flex-direction:column;gap:0.2rem;max-height:10rem;overflow:hidden;justify-content:flex-end;}
.vpm-ig-line{font-size:var(--fs-xs);line-height:1.35;color:var(--tb-text);
  text-shadow:0 1px 3px rgba(0,0,0,0.95);animation:vpmIgFade .5s ease 7s forwards;}
.vpm-ig-line .who{font-weight:800;}
.vpm-ig-line .who.red{color:var(--tb-magenta);}
.vpm-ig-line .who.blue{color:var(--tb-cyan);}
.vpm-ig-line.sys{color:var(--tb-muted);font-style:italic;}
@keyframes vpmIgFade{to{opacity:0;}}
.vpm-ig.typing .vpm-ig-line{animation:none;opacity:1;}
.vpm-ig.typing .vpm-ig-log{max-height:14rem;overflow-y:auto;}
.vpm-ig-input{pointer-events:auto;display:none;margin-top:0.2rem;background:rgba(5,8,12,0.92);
  border:1px solid var(--tb-line);border-left:0.2rem solid var(--tb-magenta);color:var(--tb-text);
  padding:0.6em 0.8em;font:inherit;font-size:var(--fs-xs);outline:none;backdrop-filter:blur(0.35rem);}
.vpm-ig.typing .vpm-ig-input{display:block;}
@media (max-width:767.98px){.vpm-ig{width:min(18rem,64vw);}}

/* ------------------------------ botón pausa ------------------------------ */
.vpm-pausebtn{position:fixed;top:max(var(--sp-3),env(safe-area-inset-top));
  right:max(var(--sp-3),env(safe-area-inset-right));z-index:15;width:2.9rem;height:2.9rem;
  background:rgba(9,14,20,0.88);color:var(--tb-text);cursor:pointer;
  font-size:var(--fs-sm);font-weight:800;backdrop-filter:blur(0.35rem);
  clip-path:polygon(0.5rem 0,100% 0,100% calc(100% - 0.5rem),calc(100% - 0.5rem) 100%,0 100%,0 0.5rem);
  transition:border-color .12s ease,color .12s ease,box-shadow .12s ease;}
@media (hover:hover) and (pointer:fine){
  .vpm-pausebtn:hover{--edge:var(--tb-magenta);color:var(--tb-magenta);box-shadow:var(--tb-glow-magenta);}
}
@media (pointer:coarse){.vpm-pausebtn{width:3.25rem;height:3.25rem;}}

/* ------------------------------ espectadores ------------------------------ */
/* Debajo de la tabla de equipos y con su propia forma: quien mira no juega, y
   meterlo en una columna lo haría parecer una plantilla más. */
.vpm-spec{display:flex;align-items:baseline;gap:var(--sp-3);flex-wrap:wrap;
  margin-top:var(--sp-3);padding:var(--sp-3);background:var(--tb-panel-2);
  border-left:2px solid var(--tb-cyan);}
.vpm-spec-k{font-family:var(--tb-font-mono);font-size:var(--fs-micro);letter-spacing:0.16em;
  font-weight:800;color:var(--tb-cyan);white-space:nowrap;}
.vpm-spec-empty{font-size:var(--fs-xs);color:var(--tb-muted);font-style:italic;}
.vpm-spec-names{font-size:var(--fs-xs);color:var(--tb-muted);}
.vpm-spec-names b{color:var(--tb-text);font-weight:700;}
.vpm-spec.drop{background:var(--tb-panel);border-left-color:var(--tb-magenta);}
.vpm-spec-back{margin-left:auto;}

/* ------------------------- botón flotante FEEDBACK ------------------------- */
/* Esquina inferior derecha. Por ENCIMA de la capa de paneles —que ocupa la
   pantalla entera aunque el menú se vea transparente, así que por debajo el
   botón no se podía ni pulsar— y por DEBAJO de los diálogos, que sí tapan a
   propósito. No se ve mientras hay un panel abierto: ahí dentro ya hay una
   entrada a FEEDBACK y flotando encima solo estorbaría. En la cancha comparte
   esquina con el medidor de ping, que se sube lo que ocupa este. */
.vpm-fab{position:fixed;bottom:max(var(--sp-3),env(safe-area-inset-bottom));
  right:max(var(--sp-3),env(safe-area-inset-right));z-index:30;
  display:none;align-items:center;gap:0.4em;padding:0.55em 0.9em;
  background:rgba(9,14,20,0.88);color:var(--tb-muted);cursor:pointer;
  font:inherit;font-size:var(--fs-xs);font-weight:800;letter-spacing:0.1em;
  backdrop-filter:blur(0.35rem);
  clip-path:polygon(0.5rem 0,100% 0,100% calc(100% - 0.5rem),calc(100% - 0.5rem) 100%,0 100%,0 0.5rem);
  transition:color .12s ease,box-shadow .12s ease;}
.vpm-fab.show{display:inline-flex;}
@media (hover:hover) and (pointer:fine){
  .vpm-fab:hover{--edge:var(--tb-cyan);color:var(--tb-cyan);box-shadow:var(--tb-glow-cyan);}
}

/* --------------------------------- modal --------------------------------- */
/* Los diálogos y los botones flotantes cuelgan de <body>, NO del panel, así que
   no heredaban su tipografía: salían con la fuente por defecto del navegador
   mientras el resto del juego iba con la suya. Se les da aquí, junto al color,
   para que "confirmar finalizar partido" se lea como parte del mismo juego. */
.vpm-modal,.vpm-fab,.vpm-pausebtn,.vpm-ig{color:var(--tb-text);font-family:var(--tb-font-ui);}
.vpm-modal{position:fixed;inset:0;z-index:40;display:flex;align-items:center;justify-content:center;
  padding:var(--sp-4);background:rgba(4,6,10,0.78);backdrop-filter:blur(0.4rem);}
.vpm-modal-card{display:flex;flex-direction:column;gap:var(--sp-2);width:min(24rem,100%);
  padding:var(--sp-5);background:linear-gradient(158deg,var(--tb-panel),var(--tb-abyss));}
.vpm-modal-row{display:flex;gap:var(--sp-2);margin-top:var(--sp-3);flex-wrap:wrap;}
.vpm-modal-row .tb-btn{flex:1 1 9rem;}

/* ------------------- fondo vivo del menú (canvas difuminado) ------------- */
/* El filtro de accesibilidad se COMPONE con el desenfoque (por eso la variable
   vale "opacity(1)" cuando no hay corrección: un "none" invalidaría la lista). */
canvas#game.tak-menu-backdrop{filter:var(--v-a11y-filter,opacity(1)) blur(0.4rem) saturate(1.25) brightness(0.95);}
.vpm-root.live{background:radial-gradient(130% 110% at 50% -10%,rgba(10,16,24,0.42),rgba(4,6,10,0.78));}

/* =========================== ADAPTACIÓN ================================== */

/* Móvil vertical: el panel ocupa todo, el contenido respira menos y el carril
   de pestañas se va abajo (lo hace el sistema, .tb-rail). */
@media (max-width:767.98px){
  .vpm-root{padding:0;}
  .vpm-panel{width:100%;height:100%;}
  .vpm-body{flex-direction:column;}
  .vpm-content{padding:var(--sp-4);}
  .vpm-teams{grid-template-columns:1fr;}
  .vpm-col{min-height:6.5rem;}
  .vpm-foot{flex-wrap:wrap;}
  .vpm-foot .tb-btn{flex:1 1 45%;}
  .vpm-menu{gap:var(--sp-4);}
}

/* Móvil apaisado: lo que sobra es ancho y lo que falta es alto. */
@media (max-height:32rem) and (orientation:landscape){
  .vpm-root{padding:0;}
  .vpm-panel{width:100%;height:100%;}
  .vpm-body{flex-direction:row;}
  .vpm-content{padding:var(--sp-3) var(--sp-4);}
  .vpm-teams{grid-template-columns:repeat(3,minmax(0,1fr));}
  .vpm-col{min-height:5rem;}
  .vpm-menu{gap:var(--sp-3);}
  .vpm-menu-actions{width:min(20rem,60vw);}
}
`;

function injectStyle(): void {
  injectThemeTokens();
  if (document.getElementById("vpm-style")) return;
  const el = document.createElement("style");
  el.id = "vpm-style";
  el.textContent = CSS;
  document.head.appendChild(el);
}

/**
 * Pestañas del panel.
 *
 * EQUIPOS incluye ahora los nombres de los equipos y las reglas del partido:
 * eran dos variables (goles y tiempo) viviendo en una pestaña propia, y los
 * nombres se editaban en un sitio distinto de donde se ven los equipos. Todo lo
 * que define "quién juega contra quién y hasta cuándo" cabe —y se entiende
 * mejor— en la misma pantalla.
 */
type Tab = "teams" | "map" | "config" | "access" | "chat";

/** A dónde va el feedback si el servidor no puede entregarlo (ver `sendFeedback`). */
const FEEDBACK_EMAIL = "dicado09cadita@gmail.com";

/** Tope del texto. Largo, porque un bug bien contado ocupa. */
const MAX_FEEDBACK = 2000;

interface ChatMsg { who: string; text: string; team: Team; sys: boolean; }

/** Primera línea de un chat recién abierto (sala nueva o partida local). */
function welcomeChat(): ChatMsg[] {
  return [{ who: "", text: "Sala nueva. Escribe /r o /b para cambiar de equipo.", team: null, sys: true }];
}

/**
 * Cuánto se espera al ECO de un mensaje propio antes de darlo por no entregado.
 *
 * Online, lo que se pinta es lo que devuelve el servidor (así el texto que se ve
 * es el moderado, y en el mismo orden para todos). Si el servidor lo descarta
 * —el presupuesto anti-spam del chat es de 6 seguidos y ~1/s— el mensaje se
 * quedaba en nada, sin eco y sin aviso: se leía como "de pronto ya no puedo
 * escribir". Pasado este plazo se dice, en vez de tragárselo.
 */
const CHAT_ECHO_TIMEOUT_MS = 3000;

/**
 * Cuánto es INTOCABLE la pantalla de fin de partido.
 *
 * No es un temporizador que la cierre: es el tiempo durante el cual nada
 * automático puede quitarla de en medio (el cambio de fase de la sala, una
 * revancha inmediata del anfitrión). Cinco segundos es lo que dura mirar el
 * marcador y entender qué ha pasado; cerrarla antes es cosa del jugador.
 */
const RESULT_LOCK_MS = 5000;

export class UIManager {
  private root: HTMLDivElement;
  private screen: Screen = "menu";
  private tab: Tab = "teams";
  private roster: Roster[] = defaultRoster();
  private chat: ChatMsg[] = welcomeChat();
  /**
   * Ámbito del chat que se está mostrando: `"local"` o `"room:<id>"`.
   *
   * El registro vivía en memoria de la UI, que sobrevive a entrar y salir de
   * salas: se acababa leyendo en una sala online lo que se había dicho en la
   * anterior (o en la partida local). El ámbito lo corta — cambiar de sala vacía
   * el registro; volver a la misma lo conserva.
   */
  private chatScope = "local";
  /** ¿Se está enseñando la pantalla de fin de partido? */
  private resultShowing = false;
  /** Hasta cuándo esa pantalla es intocable (ms epoch). Ver `showResult`. */
  private resultLockUntil = 0;
  /** Pestaña del último repintado (para saber si conservar el scroll). */
  private lastRenderedTab: Tab | null = null;
  /** Mensajes propios enviados al servidor y aún sin eco (ver `CHAT_ECHO_TIMEOUT_MS`). */
  private pendingChat: { text: string; timer: ReturnType<typeof setTimeout> }[] = [];
  private draggingId: string | null = null;
  /** Slot que se arrastra en el panel EQUIPOS online (drag de admin). */
  private draggingSlot: number | null = null;
  private pauseBtn: HTMLButtonElement | null = null;
  /** Botón flotante de FEEDBACK (esquina inferior derecha). Ver `mountFab`. */
  private fab: HTMLButtonElement | null = null;
  /** ¿Hay un panel a pantalla completa montado? (ver `syncFab`). */
  private panelOpen = false;
  private ig: HTMLDivElement | null = null;
  private igLog: HTMLDivElement | null = null;
  private igInput: HTMLInputElement | null = null;
  private igKeyHandler: ((e: KeyboardEvent) => void) | null = null;
  private typing = false;
  /** Lo tecleado y no enviado en el chat de cancha (se recupera al reabrirlo). */
  private igDraft = "";
  /** SEC-3 · Silenciados por este dispositivo (persistente, nunca al servidor). */
  private readonly muted = new MuteList();
  /** Perfil del dispositivo: icono del disco y compras (ver `lobby/profile.ts`). */
  private profile: Profile = loadProfile();
  /** Hay un cambio de icono viajando al servidor. Ver `syncProfile`. */
  private equipando = false;

  /** Cambió el nombre visible o el icono. Lo aplica quien tenga la conexión. */
  onProfileChange: ((p: { name: string; icon: string }) => void) | null = null;

  /** Icono elegido para el disco local (id del catálogo). */
  get discIcon(): string { return this.profile.icon; }

  paused = false;
  settings: Settings;

  onPlay: (() => void) | null = null;
  onPlayOnline: (() => void) | null = null;
  onResume: (() => void) | null = null;
  onRestart: (() => void) | null = null;
  onQuit: (() => void) | null = null;
  onSettingsChange: ((s: Settings) => void) | null = null;
  onApplyRules: ((r: { goalLimit: number; timeLimit: number }) => void) | null = null;
  onSelectMap: ((mapId: string) => void) | null = null;
  onLocalTeamChange: ((team: Team) => void) | null = null;
  onTypingChange: ((typing: boolean) => void) | null = null;
  getMatch: (() => MatchSnapshot | null) | null = null;
  /**
   * Entrega un mensaje de chat a la sala. Devuelve `true` si salió al servidor.
   *
   * Ese booleano es lo que decide quién pinta el mensaje: si salió, se pinta
   * cuando VUELVE difundido (el eco es el acuse de que los demás lo leyeron);
   * si no salió (partida local, o sin conexión), lo pinta la UI. Sin esta
   * distinción o se duplica el mensaje propio, o se pierde en local.
   */
  onChatSend: ((text: string) => boolean) | null = null;

  // Controles de sala online (admin): el menú de pausa los muestra según la fase.
  onRoomStart: (() => void) | null = null;
  onRoomPause: (() => void) | null = null;
  onRoomResume: (() => void) | null = null;
  onRoomFinish: (() => void) | null = null;
  /** Acciones de admin dirigidas (las valida el servidor): mover de equipo,
   *  expulsar, editar marcador y ceder admin. */
  onSetTeam: ((slot: number, team: 0 | 1) => void) | null = null;
  onKick: ((slot: number) => void) | null = null;
  /** SEC-3 · Reporta a un ocupante (el servidor lo registra, no lo difunde). */
  onReport: ((slot: number, reason: ReportReason) => void) | null = null;
  onTransferAdmin: ((slot: number) => void) | null = null;
  /** Dar/quitar admin a un slot (solo lo emite el dueño; el servidor revalida). */
  onGrantAdmin: ((slot: number) => void) | null = null;
  onRevokeAdmin: ((slot: number) => void) | null = null;
  /** Sentar a un slot a MIRAR (libera su sitio) y volver a la cancha. */
  onToSpectator: ((slot: number) => void) | null = null;
  onToPlayer: (() => void) | null = null;
  /** Contexto de sala online del admin (link + fase); null = local o invitado. */
  private onlineRoom: { phase: RoomPhase; inviteLink: string; guest?: boolean } | null = null;
  /** Lista de jugadores de la sala online (por slot) + slot local + fase; null =
   *  local. La fase decide si un no-admin puede arrastrar SU ficha (solo en lobby). */
  private onlinePeers:
    { peers: PeerInfo[]; localSlot: number; phase: RoomPhase; spectators?: string[]; spectatorCount?: number }
    | null = null;

  /** Nombres de equipo de la SALA online (null en local: se usan los ajustes). */
  private roomTeamNames: [string, string] | null = null;
  /** Cambió el nombre de un equipo (online lo aplica el servidor si eres admin). */
  onSetTeamNames: ((names: [string, string]) => void) | null = null;

  /** Nombres de equipo vigentes: los de la sala si hay, si no los locales. */
  get teamNames(): [string, string] {
    return this.roomTeamNames ?? this.settings.teamNames;
  }

  /**
   * Fija los nombres de equipo de la sala online (los difunde el servidor).
   * Pasar null en local. Refresca el panel si está abierto en esa pestaña.
   */
  setRoomTeamNames(names: [string, string] | null): void {
    const changed = names === null
      ? this.roomTeamNames !== null
      : this.roomTeamNames === null || names[0] !== this.roomTeamNames[0] || names[1] !== this.roomTeamNames[1];
    this.roomTeamNames = names;
    // No se repinta el panel mientras el jugador está escribiendo un nombre: el
    // eco del servidor llega mientras aún tiene el cursor en el otro campo, y
    // repintar destruiría el input que está usando (pierde foco y lo tecleado).
    if (changed && this.paused && this.tab === "teams" && !this.isEditingPanel()
      && this.overlayScreen === "none") {
      this.renderPanel(this.screen === "menu");
    }
  }

  /**
   * ¿Se está escribiendo en algún campo del panel ahora mismo?
   *
   * Repintar el panel destruye sus nodos, así que un repintado mientras alguien
   * teclea le quita el foco y le borra lo escrito. Pasaba de verdad: el roster
   * online se difunde con cada reporte de ping (~1/s), y ese repintado dejaba
   * el chat de la sala inservible — se escribía media frase y desaparecía. La
   * respuesta corta es no repintar mientras haya un campo con el foco; lo que
   * traía el repintado (un ping nuevo, alguien que entró) llega igual con el
   * siguiente, cuando ya no estorba.
   */
  private isEditingPanel(): boolean {
    const ae = document.activeElement;
    if (!ae) return false;
    const tag = ae.tagName;
    if (tag !== "INPUT" && tag !== "TEXTAREA") return false;
    return this.root.contains(ae);
  }

  /** Aplica un cambio de nombres desde la UI (local: ajustes; online: al servidor). */
  private setTeamNames(names: [string, string]): void {
    if (this.roomTeamNames) {
      // Online: el servidor decide; el eco de `state` actualizará roomTeamNames.
      this.onSetTeamNames?.(names);
      return;
    }
    this.settings.teamNames = names;
    saveSettings(this.settings);
    this.onSetTeamNames?.(names);
  }

  /** ¿El jugador local es admin de la sala online? (lo dice la lista de peers). */
  get localIsAdmin(): boolean {
    const info = this.onlinePeers;
    if (!info) return false;
    return info.peers.find((p) => p.slot === info.localSlot)?.isAdmin === true;
  }

  constructor(settings: Settings) {
    this.settings = settings;
    purgeLegacyRoster();
    injectStyle();
    this.root = document.createElement("div");
    this.root.className = "vpm-root";
    document.body.appendChild(this.root);
    this.mountFab();
  }

  getScreen(): Screen { return this.screen; }

  /** Nombre del jugador local (visible en EQUIPOS). */
  setPlayerName(name: string): void {
    const me = this.roster.find((r) => r.local);
    if (me) me.name = name;
  }

  /** Equipo del jugador local (0 ataque · 1 defensa · null espectador). */
  getLocalTeam(): Team { return this.roster.find((r) => r.local)?.team ?? 0; }

  /**
   * Fija el contexto de sala online para el menú de pausa: fase, link de
   * invitación (solo el admin lo tiene) y si quien mira es un INVITADO.
   *
   * El invitado también recibe contexto —antes se le pasaba `null` y por eso
   * veía el menú local— porque la sala es tan suya como del anfitrión: puede
   * elegir equipo, leer las reglas y hablar. Lo que no puede es mandar, y de eso
   * se encarga cada panel (`onlineReadOnly`) y el pie, que en vez de controles
   * de partido le dice qué se está esperando.
   */
  setOnlineRoom(info: { phase: RoomPhase; inviteLink: string; guest?: boolean } | null): void {
    this.onlineRoom = info;
    if (this.paused && this.screen === "playing" && !this.isEditingPanel()
      && this.overlayScreen === "none") this.renderPanel(false);
  }

  /** Fija la lista de jugadores de la sala online (por slot + fase). Pasar null en
   *  local. Refresca el panel EQUIPOS si está abierto (quién entró/salió, ping,
   *  y si se puede arrastrar la ficha propia según la fase). */
  setOnlinePeers(
    info:
      { peers: PeerInfo[]; localSlot: number; phase: RoomPhase; spectators?: string[]; spectatorCount?: number }
      | null,
  ): void {
    this.onlinePeers = info;
    // El roster llega ~1/s (los reportes de ping). Nunca se repinta encima de
    // alguien que está escribiendo: ver `isEditingPanel`.
    if (this.paused && this.screen === "playing" && this.tab === "teams" && !this.isEditingPanel()
      && this.overlayScreen === "none") {
      this.renderPanel(false);
    }
  }

  show(): void { this.screen = "menu"; this.paused = false; this.root.classList.add("open"); this.renderMenu(); }
  hide(): void {
    this.root.classList.remove("open");
    // El panel deja de estar en pantalla aunque nadie repinte nada.
    this.panelOpen = false;
    this.overlayScreen = "none";
    this.syncFab();
  }

  /** Transición a la pantalla de juego (sin arrancar la simulación). La usan
   *  tanto la partida local como el flujo online del lobby. */
  enterPlaying(): void {
    this.screen = "playing";
    this.panelOpen = false;
    this.overlayScreen = "none";
    this.syncFab();
    this.paused = false;
    this.hide();
    this.mountPauseBtn();
  }

  /** Arranca la partida local (transición + callback onPlay). */
  beginMatch(): void {
    this.enterPlaying();
    if (this.onPlay) this.onPlay();
  }

  private el<K extends keyof HTMLElementTagNameMap>(tag: K, cls = "", html?: string): HTMLElementTagNameMap[K] {
    const e = document.createElement(tag);
    if (cls) e.className = cls;
    if (html !== undefined) e.innerHTML = html;
    return e;
  }

  /* ---------------- MAIN MENU ---------------- */

  /**
   * Estado del fondo vivo del menú (ver `menuBackdrop.ts`): `true` si lo que se
   * ve detrás es una sala real espectada, `false` si es la exhibición local.
   * Se dice, porque no es lo mismo "hay gente jugando ahora" que "esto es una
   * demostración" — y hacer pasar lo segundo por lo primero sería un señuelo.
   */
  private backdropLive = false;

  setBackdropLive(live: boolean): void {
    if (live === this.backdropLive) return;
    this.backdropLive = live;
    const badge = this.root.querySelector(".vpm-live");
    if (badge) badge.outerHTML = this.liveBadge().outerHTML;
    const feed = this.root.querySelector(".vpm-feed-v");
    if (feed) feed.textContent = live ? "Sala en vivo" : "Exhibición local";
  }

  private liveBadge(): HTMLElement {
    return this.el("div", "vpm-live" + (this.backdropLive ? " on" : ""),
      this.backdropLive ? "● EN VIVO · PARTIDA EN CURSO" : "○ EXHIBICIÓN · PARTIDA DE MUESTRA");
  }

  /** Estado de red para la tarjeta del menú (lo fija main.ts: él sabe si hay
   *  backend configurado y en qué región). */
  private networkInfo = "LOCAL · SIN SERVIDOR";
  /**
   * Pantalla propia montada ENCIMA del panel (perfil o reportar).
   *
   * El panel se repinta solo cuando cambia la sala (roster ~1/s, cambios de
   * fase). Ese repintado tiraría abajo la pantalla que hubiera encima y te
   * dejaría en EQUIPOS a media frase — que es exactamente lo que ya pasó una vez
   * con el chat. Mientras haya una, no se repinta nada por debajo.
   */
  private overlayScreen: "none" | "profile" | "feedback" = "none";
  /** Borrador de FEEDBACK: sobrevive a cerrar la pantalla sin enviar. */
  private feedbackDraft = "";
  private feedbackKind: FeedbackKind = "bug";
  private feedbackTech = true;
  /** Resultado del último intento de envío, con las palabras que verá quien lo pulsó. */
  private feedbackStatus: { ok: boolean; msg: string } | null = null;

  setNetworkInfo(label: string): void {
    this.networkInfo = label;
    if (this.screen === "menu" && !this.paused) {
      const v = this.root.querySelector(".vpm-net-v");
      if (v) v.textContent = label;
    }
  }

  /**
   * Menú principal, en HUD de retransmisión.
   *
   * En ESCRITORIO se despliega en tres columnas simultáneas (identidad ·
   * acciones · estado de la señal), que es lo que pide una pantalla ancha: sin
   * eso, el espacio sobrante se queda vacío alrededor de tres botones. En MÓVIL
   * las columnas laterales desaparecen —el CSS las oculta, no hay JS que
   * decida— y la pantalla es solo para lo que lleva a jugar.
   *
   * Ninguna de las dos tarjetas inventa datos: el nombre es el que eligió la
   * persona y la red es la que hay configurada de verdad.
   */
  private renderMenu(): void {
    this.overlayScreen = "none";
    this.panelOpen = false;
    this.root.innerHTML = "";
    this.syncFab();
    // El menú deja ver el fondo: detrás hay una partida moviéndose y taparla
    // con un panel opaco sería tirar justo lo que la hace útil.
    this.root.classList.add("live");
    const wrap = this.el("div", "vpm-menu");

    /* --- columna izquierda: quién eres (solo escritorio) --- */
    const left = this.el("div", "vpm-menu-aside");
    const me = this.el("div", "vpm-card tb-card tb-cut-sm tb-edge tb-meta vpm-card-btn");
    me.dataset.code = "[OPR_01]";
    const glyph = iconGlyph(this.profile.icon);
    me.innerHTML = `<div class="k">Operador</div>`
      + `<div class="v">${glyph ? `<span class="vpm-op-icon">${escapeHtml(glyph)}</span>` : ""}${escapeHtml(this.playerName())}</div>`
      + `<div class="vpm-card-cta">Cambiar nombre e icono ▸</div>`;
    me.onclick = () => this.renderProfile();
    left.appendChild(me);
    const mode = this.el("div", "vpm-card tb-card tb-cut-sm tb-edge tb-meta");
    mode.dataset.code = "[MOD]";
    mode.innerHTML = `<div class="k">Modo</div><div class="v">Invitado · sin registro</div>`;
    left.appendChild(mode);
    wrap.appendChild(left);

    /* --- columna central: la marca y las tres salidas --- */
    const center = this.el("div", "vpm-menu-center");
    center.appendChild(this.el("div", "vpm-logo", "TAK <b>//</b> ATTACK"));
    center.appendChild(this.el("div", "vpm-tag", "DISCO FOOTBALL"));

    const actions = this.el("div", "vpm-menu-actions");
    const play = this.el("button", "tb-btn tb-cut-sm primary", "▶ PARTIDA LOCAL");
    play.onclick = () => this.beginMatch();
    const online = this.el("button", "tb-btn tb-cut-sm accent", "🌐 JUGAR ONLINE");
    online.onclick = () => { if (this.onPlayOnline) this.onPlayOnline(); };
    const profile = this.el("button", "tb-btn tb-cut-sm ghost", "👤 PERFIL");
    profile.onclick = () => { this.screen = "menu"; this.renderProfile(); };
    const cfg = this.el("button", "tb-btn tb-cut-sm ghost", "⚙ AJUSTES");
    cfg.onclick = () => { this.screen = "menu"; this.openConfigStandalone(); };
    // FEEDBACK no está aquí: vive en el botón fijo de la esquina, que se ve
    // desde el menú Y desde la cancha. Dos entradas a lo mismo, una de ellas
    // solo visible a veces, es una lista de acciones más larga sin ser más útil.
    actions.appendChild(play);
    actions.appendChild(online);
    actions.appendChild(profile);
    actions.appendChild(cfg);
    center.appendChild(actions);
    center.appendChild(this.liveBadge());
    wrap.appendChild(center);

    /* --- columna derecha: qué está pasando (solo escritorio) --- */
    const right = this.el("div", "vpm-menu-aside right");
    const net = this.el("div", "vpm-card tb-card tb-cut-sm tb-edge tb-meta");
    net.dataset.code = "[NET]";
    net.innerHTML = `<div class="k">Red</div><div class="v vpm-net-v">${escapeHtml(this.networkInfo)}</div>`;
    right.appendChild(net);
    const feed = this.el("div", "vpm-card tb-card tb-cut-sm tb-edge tb-meta");
    feed.dataset.code = "[FEED]";
    feed.innerHTML = `<div class="k">Señal de fondo</div>`
      + `<div class="v vpm-feed-v">${this.backdropLive ? "Sala en vivo" : "Exhibición local"}</div>`;
    right.appendChild(feed);
    wrap.appendChild(right);

    this.root.appendChild(wrap);
  }

  /**
   * FEEDBACK: bugs, ideas y opiniones, directos a quien hace el juego.
   *
   * ENVIAR manda de verdad: el mensaje va al servidor del juego (`POST
   * /feedback`), que lo entrega por correo o webhook según cómo esté
   * configurado el despliegue. Cuando no hay a dónde mandarlo —sin backend, o un
   * edge sin proveedor— se dice con esas palabras y se ofrece el correo de quien
   * escribe. Un "enviado ✓" que en realidad no sale de aquí sería peor que no
   * tener el formulario.
   *
   * Los datos técnicos se ENSEÑAN antes de mandarlos y se pueden quitar. Un
   * informe de diagnóstico que no puedes leer es lo que hace que la gente no le
   * dé a enviar.
   */
  private renderFeedback(): void {
    this.overlayScreen = "feedback";
    this.root.classList.add("open");
    this.syncFab();
    this.root.classList.remove("live");
    this.root.innerHTML = "";
    const panel = this.el("div", "vpm-panel tb-panel tb-cut tb-edge");

    const head = this.el("div", "vpm-head tb-head");
    head.appendChild(this.el("div", "vpm-title", "FEEDBACK<small>BUGS, IDEAS Y OPINIONES</small>"));
    panel.appendChild(head);

    const body = this.el("div", "vpm-body");
    const content = this.el("div", "vpm-content tb-scroll");

    content.appendChild(this.el("p", "vpm-h", "Qué nos cuentas"));
    const kinds: [FeedbackKind, string][] = [
      ["bug", "🐞 Un fallo"],
      ["idea", "💡 Una idea"],
      ["opinion", "💬 Una opinión"],
    ];
    const row = this.el("div", "vpm-fb-kinds");
    for (const [id, label] of kinds) {
      const b = this.el("button", "tb-btn tb-cut-sm" + (this.feedbackKind === id ? " accent" : " ghost"), label);
      b.onclick = () => { this.feedbackKind = id; this.renderFeedback(); };
      row.appendChild(b);
    }
    content.appendChild(row);

    const ta = this.el("textarea", "tb-input vpm-fb-text") as HTMLTextAreaElement;
    ta.rows = 7;
    ta.maxLength = MAX_FEEDBACK;
    ta.value = this.feedbackDraft;
    ta.placeholder = this.feedbackKind === "bug"
      ? "Qué hacías, qué esperabas y qué pasó. Si sabes repetirlo, cuenta los pasos."
      : "Cuéntanos. Lo leemos todo.";
    ta.setAttribute("aria-label", "Tu mensaje");
    ta.onkeydown = (e) => e.stopPropagation();
    ta.oninput = () => { this.feedbackDraft = ta.value; };
    content.appendChild(ta);

    /* --- datos técnicos, a la vista --- */
    const techRow = this.el("label", "vpm-fb-tech");
    const chk = this.el("input") as HTMLInputElement;
    chk.type = "checkbox";
    chk.checked = this.feedbackTech;
    chk.onchange = () => { this.feedbackTech = chk.checked; this.renderFeedback(); };
    techRow.appendChild(chk);
    techRow.appendChild(this.el("span", "", "Adjuntar datos técnicos (versión, navegador y los últimos pasos)"));
    content.appendChild(techRow);
    if (this.feedbackTech) {
      const pre = this.el("pre", "vpm-fb-diag", escapeHtml(this.diagnostics()));
      content.appendChild(pre);
    }
    content.appendChild(this.el("p", "vpm-hint",
      "No lleva tu nombre ni el chat. Se envía al servidor del juego, que nos lo hace llegar. "
      + "Si no hubiera a dónde mandarlo, se te ofrece abrirlo en tu correo — nunca se da por "
      + "enviado algo que no ha salido de aquí."));
    // El resultado del último intento, con sus palabras. Vive DEBAJO del texto,
    // donde estaba mirando quien acaba de pulsar enviar.
    if (this.feedbackStatus) {
      content.appendChild(this.el("p",
        `vpm-fb-status ${this.feedbackStatus.ok ? "ok" : "bad"}`,
        escapeHtml(this.feedbackStatus.msg)));
    }

    body.appendChild(content);
    panel.appendChild(body);

    const foot = this.el("div", "vpm-foot tb-foot");
    const send = this.el("button", "tb-btn tb-cut-sm accent", "✉ ENVIAR");
    const mail = this.el("button", "tb-btn tb-cut-sm", "✉ ABRIR EN MI CORREO");
    mail.onclick = () => {
      const url = `mailto:${FEEDBACK_EMAIL}`
        + `?subject=${encodeURIComponent(this.feedbackSubject())}`
        + `&body=${encodeURIComponent(this.feedbackBody())}`;
      window.open(url, "_blank");
    };
    send.onclick = () => {
      const texto = this.feedbackDraft.trim();
      if (texto.length < 3) {
        this.feedbackStatus = { ok: false, msg: "Escribe algo primero: así no hay nada que contar." };
        this.renderFeedback();
        return;
      }
      send.disabled = true;
      send.textContent = "ENVIANDO…";
      void sendFeedback({
        kind: this.feedbackKind,
        text: texto,
        diagnostics: this.feedbackTech ? this.diagnostics() : "",
      }).then((r) => {
        if (r === "enviado") {
          this.feedbackStatus = { ok: true, msg: "¡Enviado! Gracias — lo leemos todo." };
          this.feedbackDraft = "";
        } else if (r === "sin-servidor") {
          this.feedbackStatus = {
            ok: false,
            msg: "Este servidor no tiene a dónde mandarlo. Ábrelo en tu correo o cópialo: lo escrito no se ha perdido.",
          };
        } else {
          this.feedbackStatus = {
            ok: false,
            msg: "No se pudo enviar (¿sin conexión?). Vuelve a intentarlo, o ábrelo en tu correo.",
          };
        }
        this.renderFeedback();
      });
    };
    const copy = this.el("button", "tb-btn tb-cut-sm ghost", "⧉ COPIAR");
    copy.onclick = () => {
      const text = `${this.feedbackSubject()}\n\n${this.feedbackBody()}`;
      const done = (): void => {
        copy.textContent = "✓ ¡COPIADO!";
        setTimeout(() => { copy.textContent = "⧉ COPIAR"; }, 1500);
      };
      if (navigator.clipboard?.writeText) navigator.clipboard.writeText(text).then(done, done);
      else {
        const tmp = document.createElement("textarea");
        tmp.value = text;
        tmp.style.cssText = "position:fixed;left:-9999px;top:0;";
        document.body.appendChild(tmp);
        tmp.select();
        try { document.execCommand("copy"); } catch { /* noop */ }
        tmp.remove();
        done();
      }
    };
    const back = this.el("button", "tb-btn tb-cut-sm ghost", "◀ VOLVER");
    back.onclick = () => {
      this.feedbackStatus = null;
      if (this.screen === "playing") this.renderPanel(false); else this.renderMenu();
    };
    foot.appendChild(send);
    foot.appendChild(mail);
    foot.appendChild(copy);
    foot.appendChild(back);
    panel.appendChild(foot);
    this.root.appendChild(panel);
    setTimeout(() => ta.focus(), 0);
  }

  private feedbackSubject(): string {
    const etiqueta = this.feedbackKind === "bug" ? "BUG"
      : this.feedbackKind === "idea" ? "IDEA" : "OPINIÓN";
    return `[TAK//ATTACK] ${etiqueta}`;
  }

  private feedbackBody(): string {
    const texto = this.feedbackDraft.trim() || "(sin texto)";
    if (!this.feedbackTech) return texto;
    return `${texto}\n\n---- datos técnicos ----\n${this.diagnostics()}`;
  }

  /** Contexto técnico + lo que solo sabe la UI (pantalla, sala, red). */
  private diagnostics(): string {
    return diagnosticsText({
      // "pantalla" ya la usa el bloque base para el tamaño del monitor: repetir
      // la palabra para otra cosa deja un informe con dos líneas contradictorias.
      vista: this.screen,
      sala: this.onlineRoom ? `online · ${this.onlineRoom.phase}` : "local",
      red: this.networkInfo || "—",
    });
  }

  /** Nombre del jugador local (el que eligió en la pantalla de entrada). */
  private playerName(): string {
    return this.roster.find((r) => r.local)?.name ?? "TÚ";
  }

  /**
   * PERFIL. Nombre visible, icono del disco y catálogo.
   *
   * Se llega desde el menú principal (y desde la tarjeta de "Operador"), y no
   * pide cuenta: aquí se juega de invitado, así que el perfil del invitado es EL
   * perfil. Sin esto, el nombre solo se podía elegir en la pantalla de entrada —
   * una vez, y para siempre, con la errata incluida.
   *
   * El nombre se aplica EN CALIENTE: si hay partida online, viaja como mensaje
   * de perfil y el servidor lo modera y lo difunde (ver `wire.ts`), sin
   * reconectar ni salir de la sala.
   */
  private renderProfile(): void {
    // Lo primero: releer de quien manda. Entrar, salir o borrar la cuenta cambia
    // qué iconos son tuyos y cuál llevas puesto, y todos esos caminos acaban
    // repintando aquí — así ninguno se puede olvidar de refrescar.
    this.syncProfile();
    this.overlayScreen = "profile";
    this.root.classList.add("open");
    this.syncFab();
    this.root.classList.remove("live");
    this.root.innerHTML = "";
    const panel = this.el("div", "vpm-panel tb-panel tb-cut tb-edge");

    const head = this.el("div", "vpm-head tb-head");
    head.appendChild(this.el("div", "vpm-title", "PERFIL<small>CÓMO TE VE LA SALA</small>"));
    panel.appendChild(head);

    const body = this.el("div", "vpm-body");
    const content = this.el("div", "vpm-content tb-scroll");

    /* --- vista previa: tu disco tal cual sale a la cancha --- */
    content.appendChild(this.el("p", "vpm-h", "Tu disco"));
    const preview = this.el("div", "vpm-prof-preview");
    const disc = this.el("div", "vpm-prof-disc" + (this.getLocalTeam() === 1 ? " blue" : " red"),
      escapeHtml(iconGlyph(this.profile.icon)));
    preview.appendChild(disc);
    const who = this.el("div", "vpm-prof-who");
    who.appendChild(this.el("div", "vpm-prof-name", escapeHtml(this.playerName())));
    const cuenta = account.current();
    who.appendChild(this.el("div", "vpm-prof-tag",
      cuenta ? `@${escapeHtml(cuenta.nickname)}` : "INVITADO · SIN REGISTRO"));
    preview.appendChild(who);
    content.appendChild(preview);

    /* --- nombre --- */
    content.appendChild(this.el("p", "vpm-h", "Nombre visible"));
    const nameRow = this.el("div", "vpm-prof-name-row");
    const input = this.el("input", "tb-input") as HTMLInputElement;
    input.type = "text";
    input.value = this.playerName();
    input.maxLength = MAX_NAME;
    input.placeholder = "Tu nombre";
    input.setAttribute("aria-label", "Nombre visible");
    const apply = this.el("button", "tb-btn tb-cut-sm accent", "GUARDAR");
    const commitName = (): void => {
      const verdict = moderateName(input.value);
      input.value = verdict.name;
      const me = this.roster.find((r) => r.local);
      if (me) me.name = verdict.name;
      this.emitProfile();
      // El aviso solo aparece si el filtro TOCÓ algo: decir "listo" cuando no ha
      // pasado nada es ruido, y no decir nada cuando te han cambiado el nombre
      // es peor.
      const note = verdict.changed
        ? `Se ajustó a «${verdict.name}» (${verdict.reason === "profanity" ? "lenguaje" : "formato"}).`
        : "Nombre guardado.";
      this.renderProfile();
      const hint = this.root.querySelector(".vpm-prof-note");
      if (hint) hint.textContent = note;
    };
    apply.onclick = commitName;
    input.onkeydown = (e) => {
      e.stopPropagation();
      if (e.key === "Enter") commitName();
    };
    nameRow.appendChild(input);
    nameRow.appendChild(apply);
    content.appendChild(nameRow);
    content.appendChild(this.el("p", "vpm-hint vpm-prof-note",
      `Máximo ${MAX_NAME} caracteres. Si estás en una sala, el cambio se ve al instante.`));

    /* --- cuenta --- */
    if (account.enabled()) content.appendChild(this.accountBlock());

    /* --- catálogo de iconos --- */
    content.appendChild(this.el("p", "vpm-h", "Icono del disco"));
    const grid = this.el("div", "vpm-icons");
    for (const icon of DISC_ICONS) {
      grid.appendChild(this.iconCard(icon));
    }
    content.appendChild(grid);
    // Se dice ANTES de que nadie toque nada, y se dice entero: por qué no se
    // puede, qué haría falta, y que eso todavía no existe. Un catálogo que se
    // deja tocar y luego no se puede conservar es peor que uno cerrado.
    content.appendChild(this.el("p", "vpm-hint", canUseIcons()
      ? "El icono que elijas se guarda en tu cuenta, así que te acompaña al móvil y "
        + "sobrevive a limpiar datos. Los marcados con precio TODAVÍA NO SE PUEDEN "
        + "COMPRAR: la tienda no está abierta. Se anuncian para que se vea qué habrá, "
        + "no para venderte nada hoy."
      : "El icono va con tu CUENTA, no con este navegador: si no, se pierde al limpiar "
        + "datos y no te acompaña al móvil. Entra y el catálogo se abre; tu nombre, en "
        + "cambio, es tuyo desde ya y sin registrarte."));

    body.appendChild(content);
    panel.appendChild(body);

    const foot = this.el("div", "vpm-foot tb-foot");
    const back = this.el("button", "tb-btn tb-cut-sm primary", "◀ VOLVER");
    back.onclick = () => this.renderMenu();
    foot.appendChild(back);
    panel.appendChild(foot);
    this.root.appendChild(panel);
  }

  /**
   * Bloque de CUENTA dentro del perfil.
   *
   * Vive aquí y no en el menú principal por lo que se decidió desde el
   * principio: **entrar nunca es obligatorio**. Quien quiera jugar entra por un
   * enlace y está en la cancha en diez segundos; quien quiera que lo suyo le
   * acompañe, pasa por aquí. Poner el botón antes de jugar convierte una
   * comodidad en un peaje.
   */
  private accountBlock(): HTMLElement {
    const caja = this.el("div", "vpm-acc");
    const cuenta = account.current();
    caja.appendChild(this.el("p", "vpm-h", "Cuenta"));

    if (!cuenta) {
      caja.appendChild(this.el("p", "vpm-hint",
        "Tu nombre y tus partidas funcionan sin cuenta. La cuenta sirve para que "
        + "el nick sea tuyo, para que el icono te acompañe al móvil y para poder "
        + "recuperar lo que compres. No hace falta para jugar."));
      const botones = this.el("div", "vpm-acc-btns");
      const disponibles = account.providers();
      if (disponibles.length === 0) {
        // Sin proveedores NO se pinta un botón muerto: se dice. Y se dice CUÁL de
        // las dos cosas pasa, porque se arreglan en sitios distintos: una es el
        // servicio (caído, o CORS mal puesto) y la otra son unas credenciales
        // que faltan en su configuración.
        caja.appendChild(this.el("p", "vpm-hint", account.reachable()
          ? "Iniciar sesión no está disponible ahora mismo: este despliegue no "
            + "tiene ningún proveedor configurado."
          : "No se pudo contactar con el servicio de cuentas. Jugar sigue "
            + "funcionando; vuelve a intentarlo más tarde."));
      }
      for (const p of disponibles) {
        const b = this.el("button", "tb-btn tb-cut-sm primary", `ENTRAR CON ${account.nombreDe(p).toUpperCase()}`);
        b.onclick = () => {
          // Lo que traes de invitado viaja contigo: nunca empiezas de cero.
          location.href = account.startLogin(p, {
            guestId: getGuestId(),
            displayName: this.playerName(),
          });
        };
        botones.appendChild(b);
      }
      caja.appendChild(botones);
      return caja;
    }

    /* --- ya hay sesión --- */
    const fila = this.el("div", "vpm-acc-row");
    fila.appendChild(this.el("div", "vpm-acc-nick", `@${escapeHtml(cuenta.nickname)}`));
    fila.appendChild(this.el("div", "vpm-acc-meta",
      `${cuenta.coins} moneda${cuenta.coins === 1 ? "" : "s"} · entraste con `
      + cuenta.providers.map(account.nombreDe).join(", ")));
    caja.appendChild(fila);
    caja.appendChild(this.el("p", "vpm-hint",
      "El nick es permanente: es lo que impide que alguien se haga pasar por ti. "
      + "El nombre visible de arriba lo cambias cuando quieras."));

    // Vincular los proveedores que aún no estén.
    const faltan = account.providers().filter((p) => !cuenta.providers.includes(p));
    const botones = this.el("div", "vpm-acc-btns");
    for (const p of faltan) {
      const b = this.el("button", "tb-btn tb-cut-sm ghost", `VINCULAR ${account.nombreDe(p).toUpperCase()}`);
      b.onclick = () => { location.href = account.startLogin(p, { link: true }); };
      botones.appendChild(b);
    }
    // Desvincular solo tiene sentido con más de uno: quitar el último es perder
    // la cuenta, y el servicio lo rechaza igual. No se ofrece lo que no se puede.
    if (cuenta.providers.length > 1) {
      for (const p of cuenta.providers) {
        const b = this.el("button", "tb-btn tb-cut-sm ghost", `QUITAR ${account.nombreDe(p).toUpperCase()}`);
        b.onclick = () => void this.conAviso(() => account.unlink(p));
        botones.appendChild(b);
      }
    }
    const salir = this.el("button", "tb-btn tb-cut-sm ghost", "CERRAR SESIÓN");
    salir.onclick = () => void account.logout().then(() => this.renderProfile());
    botones.appendChild(salir);
    caja.appendChild(botones);

    caja.appendChild(this.el("p", "vpm-hint vpm-acc-note", ""));
    caja.appendChild(this.deleteAccountZone(cuenta.nickname));
    return caja;
  }

  /**
   * Borrar la cuenta: hay que ESCRIBIR el nick para confirmar.
   *
   * La primera versión eran dos clics en el mismo sitio ("BORRAR CUENTA" →
   * "BORRAR DE VERDAD"), y eso es una trampa: dos clics seguidos en el mismo
   * punto es exactamente lo que hace una mano nerviosa o un doble clic
   * accidental. Para lo único irreversible del juego —y que además libera el
   * nick para que se lo lleve otra persona— la fricción tiene que ser
   * proporcional al daño.
   *
   * Escribir el nick hace tres cosas de una vez: impide el accidente, obliga a
   * leer lo que va a pasar, y da un momento de pensarlo. Es lo que hace GitHub
   * para borrar un repositorio, y por los mismos motivos.
   */
  private deleteAccountZone(nickname: string): HTMLElement {
    const zona = this.el("div", "vpm-danger");
    const abrir = this.el("button", "tb-btn tb-cut-sm ghost", "Borrar mi cuenta");
    zona.appendChild(abrir);

    abrir.onclick = () => {
      abrir.remove();
      zona.appendChild(this.el("p", "vpm-hint",
        `Se borra tu correo, tus vínculos con Google y Discord, y lo que tengas. `
        + `El nick <b>@${escapeHtml(nickname)}</b> queda libre y otra persona podrá `
        + `cogerlo. <b>No se puede deshacer.</b>`));
      zona.appendChild(this.el("p", "vpm-hint",
        `Escribe <b>${escapeHtml(nickname)}</b> para confirmar:`));

      const fila = this.el("div", "vpm-prof-name-row");
      const campo = this.el("input", "tb-input") as HTMLInputElement;
      campo.type = "text";
      campo.placeholder = nickname;
      campo.setAttribute("aria-label", "Escribe tu nick para confirmar el borrado");
      const confirmar = this.el("button", "tb-btn tb-cut-sm danger", "BORRAR") as HTMLButtonElement;
      // Deshabilitado hasta que coincida: el botón no se puede pulsar por error
      // porque hasta ese momento no se puede pulsar en absoluto.
      confirmar.disabled = true;
      campo.oninput = () => { confirmar.disabled = campo.value.trim() !== nickname; };
      campo.onkeydown = (e) => { e.stopPropagation(); };
      confirmar.onclick = () => {
        if (campo.value.trim() !== nickname) return;   // cinturón y tirantes
        confirmar.disabled = true;
        confirmar.textContent = "BORRANDO…";
        void account.deleteAccount().then((ok) => {
          this.renderProfile();
          if (ok) return;
          const nota = this.root.querySelector(".vpm-acc-note");
          if (nota) nota.textContent = "No se pudo borrar la cuenta. Inténtalo más tarde.";
        });
      };
      fila.appendChild(campo);
      fila.appendChild(confirmar);
      zona.appendChild(fila);

      const cancelar = this.el("button", "tb-btn tb-cut-sm ghost", "Mejor no");
      cancelar.onclick = () => this.renderProfile();
      zona.appendChild(cancelar);
      campo.focus();
    };
    return zona;
  }

  /** Ejecuta algo de cuenta y refleja el resultado sin inventarse un "listo". */
  private async conAviso(accion: () => Promise<{ ok: boolean; message: string }>): Promise<void> {
    const r = await accion();
    this.renderProfile();
    if (!r.ok) {
      const nota = this.root.querySelector(".vpm-acc-note");
      if (nota) nota.textContent = r.message;
    }
  }

  /**
   * Elegir NICK. Solo aparece al volver de un proveedor con una cuenta que
   * todavía no existe.
   *
   * Es la única pantalla del juego que pide algo irreversible, así que lo dice
   * antes y comprueba mientras se teclea: enterarse de que el nick estaba cogido
   * después de pulsar es la peor versión de esto.
   */
  renderNickname(sugerencia = ""): void {
    this.overlayScreen = "profile";
    this.root.classList.add("open");
    this.syncFab();
    this.root.classList.remove("live");
    this.root.innerHTML = "";
    const panel = this.el("div", "vpm-panel tb-panel tb-cut tb-edge");
    const head = this.el("div", "vpm-head tb-head");
    head.appendChild(this.el("div", "vpm-title", "ELIGE TU NICK<small>ESTO NO SE CAMBIA</small>"));
    panel.appendChild(head);

    const body = this.el("div", "vpm-body");
    const content = this.el("div", "vpm-content tb-scroll");
    content.appendChild(this.el("p", "vpm-hint",
      "El nick es tu identidad estable: único, público y PERMANENTE. El nombre "
      + "que se ve sobre tu disco es otro y ese lo cambias cuando quieras."));

    const fila = this.el("div", "vpm-prof-name-row");
    const input = this.el("input", "tb-input") as HTMLInputElement;
    input.type = "text";
    input.value = sugerencia;
    input.maxLength = 16;
    input.placeholder = "tu_nick";
    input.setAttribute("aria-label", "Nick permanente");
    const crear = this.el("button", "tb-btn tb-cut-sm accent", "CREAR CUENTA") as HTMLButtonElement;
    fila.appendChild(input);
    fila.appendChild(crear);
    content.appendChild(fila);
    const nota = this.el("p", "vpm-hint vpm-nick-note", "3 a 16 caracteres: letras, números y guion bajo.");
    content.appendChild(nota);

    // Comprobación mientras se teclea, con freno: una pulsación no es una
    // consulta, y el servicio tiene un presupuesto por origen que respetar.
    let pendiente: ReturnType<typeof setTimeout> | null = null;
    const comprobar = (): void => {
      if (pendiente) clearTimeout(pendiente);
      pendiente = setTimeout(() => {
        const n = input.value.trim();
        if (n.length < 3) { nota.textContent = "3 a 16 caracteres: letras, números y guion bajo."; return; }
        void account.nicknameFree(n).then((r) => {
          nota.textContent = r.free ? `«${n}» está libre.` : (r.message || "Ese nick no vale.");
        });
      }, 350);
    };
    input.oninput = comprobar;

    const enviar = (): void => {
      crear.disabled = true;
      nota.textContent = "Creando…";
      void account.completeSignUp(input.value.trim(), {
        guestId: getGuestId(),
        displayName: this.playerName(),
      }).then((r) => {
        crear.disabled = false;
        if (r.ok) { this.renderProfile(); return; }
        nota.textContent = r.message;
      });
    };
    crear.onclick = enviar;
    input.onkeydown = (e) => {
      e.stopPropagation();
      if (e.key === "Enter") enviar();
    };

    body.appendChild(content);
    panel.appendChild(body);
    const foot = this.el("div", "vpm-foot tb-foot");
    // Salirse de aquí NO deja nada a medias: la cuenta todavía no existe.
    const luego = this.el("button", "tb-btn tb-cut-sm ghost", "AHORA NO");
    luego.onclick = () => this.renderMenu();
    foot.appendChild(luego);
    panel.appendChild(foot);
    this.root.appendChild(panel);
    input.focus();
  }

  /**
   * Abre el perfil desde fuera (al volver de un proveedor), opcionalmente con
   * un mensaje. Va EN la pantalla y no en un aviso flotante: al volver de
   * Google la persona está mirando aquí, y lo que hace falta es que lo lea, no
   * que lo vea pasar.
   */
  openProfile(mensaje = ""): void {
    this.renderProfile();
    if (!mensaje) return;
    const nota = this.root.querySelector(".vpm-acc-note") ?? this.root.querySelector(".vpm-prof-note");
    if (nota) nota.textContent = mensaje;
  }

  /** Una ficha del catálogo: disponible, bloqueada por cuenta, o de pago. */
  private iconCard(icon: DiscIcon): HTMLElement {
    const owned = isOwned(icon, this.profile.owned);
    const active = this.profile.icon === icon.id;
    // Dos motivos distintos para estar bloqueado, y no se pueden confundir:
    // "necesitas cuenta" tiene solución, "aún no está a la venta" no la tiene
    // hoy para nadie. Enseñar un precio a quien ni siquiera puede tener iconos
    // sería pedirle dinero por algo que no podría usar.
    const needsAccount = !canUseIcons() && icon.id !== NONE;
    const card = this.el("button",
      "vpm-icon tb-cut-sm tb-bevel" + (active ? " on" : "") + (owned ? "" : " locked"));
    const tag = owned ? (active ? "EN USO" : "USAR")
      : needsAccount ? "CON CUENTA"
        : priceLabel(icon);
    card.innerHTML = `<span class="g">${escapeHtml(icon.glyph || "—")}</span>`
      + `<span class="n">${escapeHtml(icon.name)}</span>`
      + `<span class="p">${escapeHtml(tag)}</span>`;
    if (owned) {
      card.onclick = () => { void this.chooseIcon(icon); };
    } else {
      card.disabled = true;
      card.title = needsAccount
        ? "Los iconos van con tu cuenta: entra y te acompañan a cualquier dispositivo"
        : "Todavía no está a la venta";
    }
    return card;
  }

  /**
   * Ponerse un icono. Con cuenta, esto es una PETICIÓN, no una escritura local.
   *
   * Se pinta de inmediato y se revierte si el servidor dice que no. El orden
   * importa: esperar a la red antes de pintar mete un retraso visible en un gesto
   * que tiene que ser instantáneo, y pintar sin comprobar dejaría la interfaz
   * enseñando un icono que la cuenta no lleva — que es justo lo que se descubre
   * al abrir el juego en el móvil y ver otra cosa.
   */
  private async chooseIcon(icon: DiscIcon): Promise<void> {
    const antes = this.profile;
    this.equipando = true;
    this.profile = { ...this.profile, icon: icon.id };
    this.emitProfile();
    this.renderProfile();
    let r: { ok: boolean; message: string };
    try {
      r = await saveProfile(this.profile);
    } finally {
      this.equipando = false;
    }
    if (r.ok) { this.renderProfile(); return; }
    this.profile = antes;
    this.emitProfile();
    this.openProfile(r.message);
  }

  /**
   * Vuelve a leer el perfil de donde manda ahora mismo.
   *
   * Hace falta porque `loadProfile()` se llama al construir la interfaz y en ese
   * momento todavía no se sabe si hay sesión: `account.load()` va por la red y
   * tarda. Sin esto, quien vuelve al juego con la sesión abierta vería su icono
   * de invitado hasta la siguiente recarga, y el icono que su cuenta lleva de
   * verdad solo aparecería en otro dispositivo.
   *
   * Solo publica si algo CAMBIÓ: se llama en cada repintado del perfil y emitir
   * siempre sería mandar una actualización de perfil por la red cada vez que se
   * abre un panel.
   */
  syncProfile(): void {
    // Mientras hay un cambio de icono viajando, la cuenta que se tiene en
    // memoria está DESACTUALIZADA a sabiendas: releerla aquí desharía el pintado
    // inmediato de `chooseIcon` justo después de hacerlo. Al volver la respuesta
    // se repinta con lo que el servidor diga, que es lo que vale.
    if (this.equipando) return;
    const fresco = loadProfile();
    const cambio = fresco.icon !== this.profile.icon;
    this.profile = fresco;
    if (cambio) this.emitProfile();
  }

  /** Publica el perfil (nombre + icono) a quien lo tenga que aplicar. */
  private emitProfile(): void {
    this.onProfileChange?.({ name: this.playerName(), icon: this.profile.icon });
  }

  /** Ajustes desde el menú principal (reusa el panel con solo pestañas relevantes). */
  private openConfigStandalone(): void {
    this.tab = "config";
    this.renderPanel(true);
  }

  /* ---------------- PAUSE / PANEL ---------------- */
  openPause(): void {
    if (this.screen !== "playing") return;
    this.paused = true;
    this.tab = "teams";
    this.root.classList.add("open");
    this.renderPanel(false);
  }

  closePause(): void {
    // La celebración de fin de partido no se cierra por un cambio de fase de la
    // sala ni por un Esc perdido durante los primeros segundos.
    if (this.resultLocked) return;
    this.resultShowing = false;
    this.paused = false;
    this.hide();
    if (this.onResume) this.onResume();
  }

  private renderPanel(configOnly: boolean): void {
    this.overlayScreen = "none";
    this.panelOpen = true;
    this.root.classList.add("open");
    this.syncFab();
    this.root.classList.remove("live"); // el panel sí tapa: aquí se está leyendo
    // Dónde estaba mirando quien tiene el panel abierto. Cada cambio de un
    // ajuste repinta el panel entero, y sin esto el scroll volvía arriba a cada
    // clic: para tocar tres interruptores seguidos había que bajar tres veces.
    // Se conserva por pestaña — cambiar de pestaña sí empieza arriba, que es lo
    // que se espera de una pantalla nueva.
    const keepScroll = this.tab === this.lastRenderedTab
      ? this.root.querySelector<HTMLElement>(".vpm-content")?.scrollTop ?? 0
      : 0;
    this.lastRenderedTab = this.tab;
    this.root.innerHTML = "";
    const panel = this.el("div", "vpm-panel tb-panel tb-cut tb-edge");

    /* header */
    const head = this.el("div", "vpm-head tb-head");
    head.appendChild(this.el("div", "vpm-title", `${configOnly ? "AJUSTES" : "PARTIDA"}<small>${configOnly ? "CONFIGURACIÓN DEL JUGADOR" : "EN PAUSA"}</small>`));
    const snap = this.getMatch ? this.getMatch() : null;
    if (!configOnly && snap) {
      // Mismo marcador simétrico que el HUD, con los nombres de equipo: el
      // jugador no debería tener que traducir entre dos formas de leer el
      // resultado según qué pantalla mire.
      const score = this.el("div", "vpm-score");
      const names = this.teamNames;
      score.appendChild(this.el("div", "vpm-scteam red", escapeHtml(names[0])));
      score.appendChild(this.el("div", "vpm-scnum red", String(snap.score[0])));
      const clock = this.el("div", "vpm-clock");
      const remaining = snap.timeLimit > 0 ? snap.timeLimit - snap.matchTime : snap.matchTime;
      clock.appendChild(this.el("b", "", fmtTime(remaining)));
      clock.appendChild(this.el("span", "", snap.timeLimit > 0 ? "RESTANTE" : "TIEMPO"));
      score.appendChild(clock);
      score.appendChild(this.el("div", "vpm-scnum blue", String(snap.score[1])));
      score.appendChild(this.el("div", "vpm-scteam blue", escapeHtml(names[1])));
      head.appendChild(score);
    }
    // Botón minimalista de invitación arriba-derecha (host online): copia el link
    // SIN mostrar la URL. Se apoya en margin-left:auto si no hubo score.
    if (!configOnly && this.onlineRoom?.inviteLink) head.appendChild(this.renderCopyInvite(this.onlineRoom.inviteLink));
    // CERRAR va en la esquina superior derecha, no en la fila de acciones del
    // partido. Es la convención de cualquier panel —y sobre todo, no es una
    // acción DEL PARTIDO: ponerlo entre REANUDAR, REINICIAR y ABANDONAR obligaba
    // a leer cuatro botones para encontrar el que solo cierra una ventana.
    if (!configOnly) {
      const x = this.el("button", "tb-bevel vpm-close", "✕");
      x.title = "Cerrar (Esc)";
      x.setAttribute("aria-label", "Cerrar el menú");
      x.onclick = () => this.closePause();
      head.appendChild(x);
    }
    panel.appendChild(head);

    /* body: rail + content */
    const body = this.el("div", "vpm-body");
    const rail = this.el("div", "tb-rail");
    // AJUSTES global = CONFIG + ACCESO. La cancha, los equipos y las reglas son
    // OPCIONES DE SALA del host (van en el menú de pausa durante la partida), no
    // en la config global. CONFIG y ACCESO son del JUGADOR, no de la sala, así
    // que están también desde el menú principal — antes de entrar a jugar, que
    // es cuando alguien que los necesita va a buscarlos.
    const tabs: [Tab, string][] = configOnly
      ? [["config", "CONFIG"], ["access", "ACCESO"]]
      : [["teams", "EQUIPOS"], ["map", "MAPA"], ["config", "CONFIG"], ["access", "ACCESO"], ["chat", "CHAT"]];
    if (configOnly && this.tab !== "config" && this.tab !== "access") this.tab = "config";
    for (const [id, label] of tabs) {
      const b = this.el("button", "tb-tab" + (this.tab === id ? " active" : ""), label);
      b.onclick = () => { this.tab = id; this.renderPanel(configOnly); };
      rail.appendChild(b);
    }
    body.appendChild(rail);

    const content = this.el("div", "vpm-content tb-scroll");
    switch (this.tab) {
      case "teams": this.renderTeams(content); break;
      case "map": this.renderMaps(content); break;
      case "config": this.renderConfig(content); break;
      case "access": this.renderAccess(content); break;
      case "chat": this.renderChat(content); break;
    }
    body.appendChild(content);
    // ESCRITORIO · densidad: con ancho de sobra, la conversación de la sala no
    // tiene por qué esconderse detrás de una pestaña. Se monta siempre y la
    // enseña el CSS a partir de 80rem (por debajo, CHAT sigue siendo su propia
    // pestaña). El log se rellena solo: `pushChat` escribe en TODOS los que
    // estén montados, así que las dos vistas del chat nunca divergen.
    if (!configOnly && this.tab !== "chat") body.appendChild(this.renderSideChat());
    panel.appendChild(body);

    /* footer */
    const foot = this.el("div", "vpm-foot tb-foot");
    if (configOnly) {
      const back = this.el("button", "tb-btn tb-cut-sm primary", "◀ VOLVER");
      back.onclick = () => this.renderMenu();
      foot.appendChild(back);
    } else if (this.onlineRoom?.guest) {
      // INVITADO: no manda en la sala, así que no se le enseñan controles que no
      // hacen nada. Se le dice en qué punto está el partido y se le deja cerrar
      // el menú o irse.
      const phase = this.onlineRoom.phase;
      const estado = phase === "lobby" ? "El anfitrión está montando el partido. Coloca tu ficha y habla mientras tanto."
        : phase === "countdown" ? "El partido arranca en unos segundos."
          : phase === "paused" ? "Partido en pausa por el anfitrión."
            : phase === "over" ? "Partido terminado. El anfitrión puede empezar otro."
              : "Partido en juego.";
      foot.appendChild(this.el("p", "vpm-foot-note", estado));
      // Con el partido terminado se enseña la REVANCHA aunque no se pueda pulsar:
      // que exista la opción es información, y sin ella lo único que quedaba en
      // pantalla era ABANDONAR — como si el juego se hubiera acabado del todo.
      if (phase === "over") foot.appendChild(this.rematchButton(true));
      foot.appendChild(this.quitButton());
    } else if (this.onlineRoom) {
      // Controles de sala online (host), según la fase.
      const btn = (label: string, cls: string, on: (() => void) | null): HTMLButtonElement => {
        const b = this.el("button", `tb-btn tb-cut-sm ${cls}`, label);
        b.onclick = () => on?.();
        return b;
      };
      const phase = this.onlineRoom.phase;
      if (phase === "lobby") foot.appendChild(btn("▶ EMPEZAR PARTIDO", "primary", this.onRoomStart));
      else if (phase === "playing") {
        foot.appendChild(btn("⏸ PAUSA", "primary", this.onRoomPause));
        // CERRAR ya no vive aquí: está arriba a la derecha, donde se busca.
        //
        // Y aquí va FINALIZAR, no REINICIAR. Reiniciar en mitad de un partido
        // borraba el marcador de toda la sala para volver al minuto cero sin
        // que nadie viera un resultado: el trabajo de los que iban ganando,
        // deshecho. Finalizar cierra el partido como lo cierra el reloj —con su
        // pantalla de resultado— y desde ahí la REVANCHA ya empieza uno nuevo.
        foot.appendChild(this.finishButton());
      } else if (phase === "paused") {
        // Volver a jugar es la acción de SEGUIR: turquesa, el color de "adelante".
        foot.appendChild(btn("▶ REANUDAR", "accent", this.onRoomResume));
        foot.appendChild(this.finishButton());
      } else if (phase === "over") {
        // Faltaba esta rama entera: con el partido terminado, el menú de sala se
        // quedaba con ABANDONAR y nada más. Cualquiera que llegara aquí sin la
        // pantalla de resultado delante —entrar a una sala ya terminada, por
        // ejemplo— se encontraba con que la única salida era irse.
        foot.appendChild(this.rematchButton(false));
      }
      foot.appendChild(this.quitButton());
    } else {
      const resume = this.el("button", "tb-btn tb-cut-sm accent", "▶ REANUDAR");
      resume.onclick = () => this.closePause();
      foot.appendChild(resume);
      foot.appendChild(this.restartButton());
      foot.appendChild(this.quitButton());
    }
    panel.appendChild(foot);
    this.root.appendChild(panel);
    if (keepScroll > 0) content.scrollTop = keepScroll;
  }

  /** Botón minimalista "COPIAR INVITACIÓN" (arriba-derecha del header). NO muestra
   *  la URL: al pulsarlo copia el link (que ya incluye ?join=…&region=…) al
   *  portapapeles y da feedback "¡COPIADO!". */
  private renderCopyInvite(link: string): HTMLElement {
    const btn = this.el("button", "tb-btn tb-cut-sm accent vpm-copy", "🔗 COPIAR INVITACIÓN");
    btn.title = "Copiar el enlace para invitar";
    btn.onclick = () => {
      const done = (): void => {
        btn.textContent = "✓ ¡COPIADO!";
        btn.classList.add("done");
        setTimeout(() => { btn.textContent = "🔗 COPIAR INVITACIÓN"; btn.classList.remove("done"); }, 1500);
      };
      if (navigator.clipboard?.writeText) navigator.clipboard.writeText(link).then(done, done);
      else {
        // Fallback sin Clipboard API: input temporal fuera de pantalla.
        const ta = document.createElement("textarea");
        ta.value = link;
        ta.style.cssText = "position:fixed;left:-9999px;top:0;";
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand("copy"); } catch { /* noop */ }
        ta.remove();
        done();
      }
    };
    return btn;
  }

  /**
   * Botón de REVANCHA del menú de sala. `readOnly` = se ve pero no se pulsa
   * (invitado): saber que la opción existe y de quién depende vale más que un
   * hueco vacío.
   */
  private rematchButton(readOnly: boolean): HTMLButtonElement {
    const b = this.el("button", "tb-btn tb-cut-sm primary", "⟳ REVANCHA");
    if (readOnly) {
      b.disabled = true;
      b.title = "Solo el anfitrión puede empezar otro partido";
    } else {
      b.onclick = () => { this.paused = false; this.hide(); this.onRestart?.(); };
    }
    return b;
  }

  /**
   * Botón de FINALIZAR (host online): cierra el partido con el marcador que hay.
   *
   * Pregunta antes porque termina el partido de TODA la sala, no solo el tuyo —
   * y a diferencia de reiniciar, lo que hace es dar un resultado, no borrarlo.
   */
  private finishButton(): HTMLButtonElement {
    const b = this.el("button", "tb-btn tb-cut-sm", "🏁 FINALIZAR");
    b.onclick = () => this.confirm({
      title: "¿Finalizar el partido?",
      body: "El partido termina para toda la sala con el marcador actual y se muestra el resultado. Después podéis pedir la revancha.",
      confirmLabel: "SÍ, FINALIZAR",
      cancelLabel: "CANCELAR",
      onConfirm: () => this.onRoomFinish?.(),
    });
    return b;
  }

  /** Botón de REINICIAR: pregunta antes, porque borra el marcador de la sala. */
  private restartButton(): HTMLButtonElement {
    const b = this.el("button", "tb-btn tb-cut-sm", "⟳ REINICIAR");
    b.onclick = () => this.confirmRestart(() => {
      this.paused = false;
      this.hide();
      this.onRestart?.();
    });
    return b;
  }

  /**
   * Botón de ABANDONAR: rojo de advertencia y con confirmación.
   *
   * Va pegado a REANUDAR y a REINICIAR, y hasta ahora salirse de la partida
   * costaba exactamente lo mismo que seguir jugando: un clic, sin vuelta atrás.
   * El color lo separa de las otras dos acciones y la confirmación cubre el
   * resbalón — que en una sala online no te saca solo a ti de la pantalla, te
   * saca de la partida en la que hay más gente esperando.
   */
  private quitButton(): HTMLButtonElement {
    const quit = this.el("button", "tb-btn tb-cut-sm danger", "✕ ABANDONAR");
    quit.onclick = () => this.confirmQuit();
    return quit;
  }

  /**
   * Diálogo de confirmación, con la misma piel que el resto del juego (panel
   * achaflanado, filo de neón, botones del sistema).
   *
   * Es uno solo para todas las preguntas destructivas — abandonar, reiniciar —
   * porque una confirmación con otro aspecto se lee como un cartel del navegador
   * y la gente la despacha sin leerla. Esc y el clic fuera SIEMPRE cancelan, y
   * el foco arranca en la opción segura: un Enter distraído no puede romper nada.
   */
  private confirm(opts: {
    title: string;
    body: string;
    /** Texto del botón peligroso (rojo). */
    confirmLabel: string;
    /** Texto del botón seguro (turquesa). */
    cancelLabel?: string;
    onConfirm: () => void;
  }): void {
    const back = this.el("div", "vpm-modal");
    const card = this.el("div", "vpm-modal-card tb-cut tb-edge");
    card.appendChild(this.el("p", "vpm-h", opts.title));
    card.appendChild(this.el("p", "vpm-hint", opts.body));

    const close = (): void => {
      back.remove();
      window.removeEventListener("keydown", onKey, true);
    };
    function onKey(e: KeyboardEvent): void {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      close();
    }

    const row = this.el("div", "vpm-modal-row");
    const stay = this.el("button", "tb-btn tb-cut-sm accent", opts.cancelLabel ?? "SEGUIR JUGANDO");
    stay.onclick = () => close();
    const go = this.el("button", "tb-btn tb-cut-sm danger", opts.confirmLabel);
    go.onclick = () => { close(); opts.onConfirm(); };
    row.appendChild(stay);
    row.appendChild(go);
    card.appendChild(row);
    back.appendChild(card);
    back.onclick = (e) => { if (e.target === back) close(); };
    document.body.appendChild(back);
    window.addEventListener("keydown", onKey, true);
    setTimeout(() => stay.focus(), 0);
  }

  /** Confirmación de abandono (Esc y clic fuera = quedarse). */
  private confirmQuit(): void {
    const online = this.onlinePeers !== null || this.onlineRoom !== null;
    this.confirm({
      title: "¿Seguro que quieres abandonar?",
      body: online
        ? "Saldrás de la sala y volverás al menú principal. Puedes volver a entrar con el mismo enlace, pero la partida sigue sin ti mientras tanto."
        : "Se perderá el marcador de esta partida y volverás al menú principal.",
      confirmLabel: "SÍ, ABANDONAR",
      onConfirm: () => this.toMenu(),
    });
  }

  /**
   * Confirmación de reinicio. Reiniciar borra el marcador de todo el mundo, no
   * solo el tuyo: online lo aplica el servidor para la sala entera, así que un
   * resbalón le cuesta el partido a los demás.
   */
  private confirmRestart(run: () => void): void {
    this.confirm({
      title: "¿Reiniciar el partido?",
      body: this.onlinePeers !== null
        ? "El marcador vuelve a 0 para TODA la sala y el partido empieza de nuevo."
        : "El marcador vuelve a 0 y el partido empieza de nuevo.",
      confirmLabel: "SÍ, REINICIAR",
      cancelLabel: "CANCELAR",
      onConfirm: run,
    });
  }

  /**
   * Salida FORZADA de la partida, sin preguntar.
   *
   * Es lo que hace falta cuando la decisión no es del jugador —una expulsión—:
   * confirmar "¿seguro que quieres abandonar?" a quien ya han echado sería una
   * broma pesada, y dejarle en la cancha con la conexión muerta, peor.
   */
  leaveToMenu(): void {
    this.hideResult();
    // Un diálogo abierto ("¿seguro que quieres abandonar?") quedaría flotando
    // sobre el lobby preguntando por una partida de la que ya no formamos parte.
    for (const m of document.querySelectorAll(".vpm-modal")) m.remove();
    this.toMenu();
  }

  private toMenu(): void {
    this.screen = "menu";
    this.paused = false;
    this.removePauseBtn();
    if (this.onQuit) this.onQuit();
    this.renderMenu();
  }

  /* ---------------- TEAMS (formación + nombres + reglas) ---------------- */
  /**
   * EQUIPOS: quién juega en cada bando y hasta cuándo dura el partido.
   *
   * Los NOMBRES de los equipos ya no tienen sección propia: se editan donde se
   * leen, en la cabecera de cada columna. Aquel bloque ocupaba un tercio de la
   * pantalla para repetir dos palabras que ya estaban tres centímetros más
   * abajo, y las listas de jugadores —lo único que de verdad se mira aquí—
   * quedaban aplastadas contra el fondo del panel.
   */
  private renderTeams(c: HTMLElement): void {
    if (this.onlinePeers) this.renderOnlineTeams(c, this.onlinePeers);
    else this.renderLocalTeams(c);
    this.renderRules(c);
  }

  /**
   * Cabecera de una columna de equipo: el nombre (editable in situ por quien
   * manda) y cuánta gente hay.
   *
   * Editable = partida local, u online siendo admin. Al invitado se le enseña el
   * nombre tal cual, no un campo muerto: saber cómo se llama tu equipo no es un
   * privilegio, y un control que no responde se lee como un juego roto.
   */
  private teamColHeader(team: 0 | 1, count: number): HTMLElement {
    const h = this.el("div", "vpm-col-h");
    const name = this.teamNames[team];
    if (this.onlineReadOnly) {
      h.appendChild(this.el("span", "vpm-col-name ro", escapeHtml(name)));
    } else {
      const input = this.el("input", "vpm-col-name") as HTMLInputElement;
      input.type = "text";
      input.value = name;
      input.maxLength = MAX_TEAM_NAME;
      input.placeholder = DEFAULT_TEAM_NAMES[team];
      input.title = "Cambiar el nombre del equipo";
      input.setAttribute("aria-label", `Nombre del equipo ${team === 0 ? "rojo" : "turquesa"}`);
      // El commit va al salir del campo o con Enter, no en cada tecla: online
      // esto viaja al servidor y se difunde a la sala entera.
      const commit = (): void => {
        const next: [string, string] = [this.teamNames[0], this.teamNames[1]];
        next[team] = moderateTeamName(input.value, team);
        input.value = next[team];
        this.setTeamNames(next);
      };
      input.onblur = commit;
      input.onkeydown = (e) => {
        e.stopPropagation();
        if (e.key === "Enter") { commit(); input.blur(); }
      };
      h.appendChild(input);
    }
    h.appendChild(this.el("span", "vpm-col-n", String(count)));
    return h;
  }

  private renderLocalTeams(c: HTMLElement): void {
    const names = this.teamNames;
    c.appendChild(this.el("p", "vpm-h", "Formación · arrastra a los jugadores"));
    const grid = this.el("div", "vpm-teams");
    // Las columnas llevan el NOMBRE del equipo, no un rol inventado: el
    // marcador, la cancha y esta pantalla tienen que hablar del mismo equipo
    // con la misma palabra.
    const cols: [Team, string][] = [
      [0, "red"],
      [null, "spec"],
      [1, "blue"],
    ];
    for (const [team, cls] of cols) {
      const col = this.el("div", `vpm-col tb-cut-sm ${cls}`);
      const members = this.roster.filter((r) => r.team === team);
      col.appendChild(team === null
        ? this.el("div", "vpm-col-h", `<span class="vpm-col-name ro">ESPECTADORES</span><span class="vpm-col-n">${members.length}</span>`)
        : this.teamColHeader(team, members.length));
      const list = this.el("div", "vpm-col-list");
      for (const m of members) {
        const chip = this.el("div", "vpm-chip tb-chip tb-cut-sm" + (m.local ? " me" : ""), `<i class="vpm-dot"></i>${escapeHtml(m.name)}`);
        chip.draggable = true;
        chip.ondragstart = () => { this.draggingId = m.id; chip.classList.add("dragging"); };
        chip.ondragend = () => { this.draggingId = null; chip.classList.remove("dragging"); };
        list.appendChild(chip);
      }
      col.appendChild(list);
      col.ondragover = (e) => { e.preventDefault(); col.classList.add("drop"); };
      col.ondragleave = () => col.classList.remove("drop");
      col.ondrop = (e) => {
        e.preventDefault();
        col.classList.remove("drop");
        this.assignTeam(this.draggingId, team);
      };
      grid.appendChild(col);
    }
    c.appendChild(grid);
    c.appendChild(this.el("p", "vpm-hint",
      `Tu ficha (TÚ) cambia el color de tu disco en cancha al moverla entre ${escapeHtml(names[0])} y ${escapeHtml(names[1])}. `
      + "Los nombres de los equipos se editan pulsando sobre ellos, arriba de cada columna."));
  }

  /** Formación online: columnas ROJO (ataque) / TURQUESA (defensa) con TODOS los
   *  jugadores de la sala por su equipo, con su ping. El ADMIN puede arrastrar a
   *  cualquiera (la acción la valida el servidor) y expulsar. Un NO-admin puede
   *  arrastrar SU PROPIA ficha solo en fase "lobby" (TAREA D); el servidor lo
   *  vuelve a validar. Empezada la partida, el no-admin queda en solo-lectura. */
  private renderOnlineTeams(
    c: HTMLElement,
    info: { peers: PeerInfo[]; localSlot: number; phase: RoomPhase; spectators?: string[]; spectatorCount?: number },
  ): void {
    const admin = this.localIsAdmin;
    // DUEÑO de la sala: el que la creó. Es el único que reparte el rol de admin,
    // así que los botones de ★ solo salen para él.
    const soyDueno = info.peers.find((p) => p.slot === info.localSlot)?.isOwner === true;
    const inLobby = info.phase === "lobby";
    // ¿Puede el jugador local arrastrar el slot `s`? El admin, cualquiera; un
    // no-admin, solo su propio slot y solo en lobby.
    const canDrag = (s: number): boolean => admin || (inLobby && s === info.localSlot);
    const dropEnabled = admin || inLobby; // el no-admin necesita drop para su ficha
    c.appendChild(this.el("p", "vpm-h", admin
      ? "Jugadores · arrastra entre equipos (admin)"
      : inLobby ? "Jugadores · arrastra TU ficha para cambiar de equipo"
        : "Jugadores en la sala (solo lectura)"));
    const grid = this.el("div", "vpm-teams");
    grid.style.gridTemplateColumns = "1fr 1fr";
    const roomNames = this.teamNames;
    const cols: [0 | 1, string][] = [[0, "red"], [1, "blue"]];
    for (const [team, cls] of cols) {
      const col = this.el("div", `vpm-col tb-cut-sm ${cls}`);
      const members = info.peers.filter((p) => (p.team ?? (p.slot % 2)) === team);
      col.appendChild(this.teamColHeader(team, members.length));
      const list = this.el("div", "vpm-col-list");
      if (members.length === 0) {
        list.appendChild(this.el("div", "vpm-chip tb-chip tb-cut-sm", `<i class="vpm-dot"></i><span style="opacity:.6;font-style:italic;">vacío</span>`));
      }
      for (const m of members) {
        const isLocal = m.slot === info.localSlot;
        // Se distingue al dueño (♛) del resto de admins (★): son roles con
        // poderes distintos y llamarlos igual haría que el de al lado pareciera
        // capaz de expulsar a quien montó la sala.
        const tags = (m.isOwner ? " ♛" : m.isAdmin ? " ★" : "") + (isLocal ? " (TÚ)" : "");
        const chip = this.el("div", "vpm-chip tb-chip tb-cut-sm" + (isLocal ? " me" : ""), `<i class="vpm-dot"></i>${escapeHtml(m.name)}${tags}`);
        chip.appendChild(this.pingBadge(m.pingMs));
        if (canDrag(m.slot)) {
          chip.draggable = true;
          chip.style.cursor = "grab";
          chip.ondragstart = () => { this.draggingSlot = m.slot; chip.classList.add("dragging"); };
          chip.ondragend = () => { this.draggingSlot = null; chip.classList.remove("dragging"); };
        } else {
          chip.style.cursor = "default";
        }
        // SEC-3 · Silenciar y reportar: para CUALQUIERA, no solo el admin. Es
        // justo lo que necesita quien no manda en la sala y está sufriendo a
        // alguien — el admin ya tiene la expulsión.
        if (!isLocal) {
          const muted = this.muted.isMuted(m.muteId);
          const mute = this.el("button", "tb-bevel vpm-mod" + (muted ? " on" : ""), muted ? "🔇" : "🔈");
          mute.title = muted ? "Dejar de silenciar" : "Silenciar (solo para ti)";
          mute.onclick = (e) => {
            e.stopPropagation();
            if (!m.muteId) return;
            this.muted.toggle(m.muteId);
            this.renderPanel(this.screen === "menu");
          };
          chip.appendChild(mute);

          const report = this.el("button", "tb-bevel vpm-mod", "⚑");
          report.title = "Reportar";
          report.onclick = (e) => { e.stopPropagation(); this.openReport(m); };
          chip.appendChild(report);
        }
        // Dar o quitar admin: SOLO el dueño de la sala, y nunca sobre sí mismo.
        // El dueño no puede degradarse por accidente y dejar la sala sin nadie
        // que mande.
        if (soyDueno && !isLocal) {
          const esAdmin = m.isAdmin === true;
          const star = this.el("button", "tb-bevel vpm-mod" + (esAdmin ? " on" : ""), "★");
          star.title = esAdmin ? "Quitar admin" : "Dar admin";
          star.onclick = (e) => {
            e.stopPropagation();
            if (esAdmin) this.onRevokeAdmin?.(m.slot); else this.onGrantAdmin?.(m.slot);
          };
          chip.appendChild(star);
        }
        // Expulsar: solo el admin, y no a sí mismo.
        if (admin && !isLocal) {
          const kick = this.el("button", "tb-bevel vpm-kick", "✕");
          kick.title = "Expulsar";
          kick.onclick = (e) => { e.stopPropagation(); this.onKick?.(m.slot); };
          chip.appendChild(kick);
        }
        list.appendChild(chip);
      }
      col.appendChild(list);
      if (dropEnabled) {
        col.ondragover = (e) => { e.preventDefault(); col.classList.add("drop"); };
        col.ondragleave = () => col.classList.remove("drop");
        col.ondrop = (e) => {
          e.preventDefault();
          col.classList.remove("drop");
          // Solo emite si el slot arrastrado es arrastrable por este jugador (el
          // servidor lo revalida de todas formas).
          if (this.draggingSlot !== null && canDrag(this.draggingSlot)) this.onSetTeam?.(this.draggingSlot, team);
          this.draggingSlot = null;
        };
      }
      grid.appendChild(col);
    }
    c.appendChild(grid);
    // ESPECTADORES: debajo de la tabla, porque no juegan pero están. Sin esto,
    // en la sala había gente mirando que no existía en ninguna pantalla.
    const espectadores = info.spectators ?? [];
    // El total incluye a los anónimos: quien tiene el menú abierto está viendo
    // esta partida de fondo, y cuenta como público aunque no dé su nombre.
    const total = Math.max(info.spectatorCount ?? 0, espectadores.length);
    const specRow = this.el("div", "vpm-spec");
    // Zona de SOLTAR: arrastrar una ficha aquí sienta a esa persona a mirar y
    // libera su sitio. Es la tercera "columna" de la sala, aunque no juegue.
    if (dropEnabled) {
      specRow.ondragover = (e) => { e.preventDefault(); specRow.classList.add("drop"); };
      specRow.ondragleave = () => specRow.classList.remove("drop");
      specRow.ondrop = (e) => {
        e.preventDefault();
        specRow.classList.remove("drop");
        if (this.draggingSlot !== null && canDrag(this.draggingSlot)) this.onToSpectator?.(this.draggingSlot);
        this.draggingSlot = null;
      };
    }
    specRow.appendChild(this.el("span", "vpm-spec-k", `👁 MIRANDO · ${total}`));
    if (total === 0) {
      specRow.appendChild(this.el("span", "vpm-spec-empty", "nadie de público ahora mismo"));
    } else {
      const names = this.el("span", "vpm-spec-names");
      // Tope de nombres: una lista larga no cabe y tampoco dice más que un número.
      const visibles = espectadores.slice(0, 8);
      const anonimos = total - espectadores.length;
      const extra = espectadores.length - visibles.length + anonimos;
      names.innerHTML = (visibles.map((n) => `<b>${escapeHtml(n)}</b>`).join(" · ") || "")
        + (extra > 0 ? `${visibles.length > 0 ? " · " : ""}+${extra} sin nombre` : "");
      specRow.appendChild(names);
    }
    // Quien está mirando y quiere jugar: su propio botón. El sitio lo da el
    // servidor si queda alguno libre.
    if (info.localSlot < 0) {
      const back = this.el("button", "tb-btn tb-cut-sm accent vpm-spec-back", "▸ VOLVER A JUGAR");
      back.onclick = () => this.onToPlayer?.();
      specRow.appendChild(back);
    }
    c.appendChild(specRow);
    c.appendChild(this.el("p", "vpm-hint", admin
      ? "Arrastra a un jugador a la otra columna para cambiarlo de equipo, o a MIRANDO para sentarlo de público. Pulsa el nombre de un equipo, arriba, para cambiarlo. ♛ = dueño de la sala · ★ = admin."
      : inLobby ? `Arrastra TU ficha entre ${escapeHtml(roomNames[0])} y ${escapeHtml(roomNames[1])}, o a MIRANDO para ver el partido de público. ♛ = dueño · ★ = admin.`
        : "Solo los admins (★) pueden cambiar equipos, nombres y reglas; el dueño (♛) además reparte el rol."));
  }

  /** Etiqueta de ping (ms) para un chip del roster: color por umbral, "—" si aún
   *  no hay medición de ese jugador. */
  private pingBadge(ms?: number): HTMLElement {
    const badge = this.el("span", "vpm-ping");
    if (ms === undefined) {
      badge.textContent = "—";
      badge.style.cssText = "margin-left:auto;font-size:calc(10px * var(--v-scale,1));font-weight:700;color:#7b8a97;font-variant-numeric:tabular-nums;";
      return badge;
    }
    const col = ms < 60 ? "#3affd4" : ms < 120 ? "#ffcc55" : "#ff4655";
    badge.textContent = `${ms} ms`;
    badge.style.cssText = `margin-left:auto;font-size:calc(10px * var(--v-scale,1));font-weight:800;color:${col};font-variant-numeric:tabular-nums;`;
    return badge;
  }

  private assignTeam(id: string | null, team: Team): void {
    if (!id) return;
    const p = this.roster.find((r) => r.id === id);
    if (!p || p.team === team) return;
    p.team = team;
    if (p.local && this.onLocalTeamChange) this.onLocalTeamChange(team);
    const dest = team === 0 ? this.teamNames[0] : team === 1 ? this.teamNames[1] : "ESPECTADORES";
    this.pushChat(`${p.name} se movió a ${dest}`, null, true);
    // Solo re-renderiza el panel de pausa si está abierto (no desde el chat in-game).
    if (this.paused) this.renderPanel(false);
  }

  /** En online sin ser admin, los paneles de sala (reglas/mapa/equipos) son de
   *  solo lectura: solo el admin puede cambiarlos (server-authoritative). */
  private get onlineReadOnly(): boolean {
    return this.onlinePeers !== null && !this.localIsAdmin;
  }

  /** Aviso de solo-lectura para no-admins en los paneles de sala. */
  private readOnlyNote(): HTMLElement {
    return this.el("p", "vpm-hint", "🔒 Solo el admin de la sala puede cambiar esto.");
  }

  /* ---------------- MATCH RULES ---------------- */
  /**
   * Reglas del partido: las dos únicas variables que hay (goles y tiempo).
   *
   * Viven al final de EQUIPOS y no en una pestaña propia: una pestaña entera
   * para dos steppers obligaba a saltar de pantalla para montar una partida.
   */
  private renderRules(c: HTMLElement): void {
    c.appendChild(this.el("p", "vpm-h", "Reglas del partido"));
    if (this.onlineReadOnly) {
      const g = this.settings.goalLimit, t = this.settings.timeLimit;
      c.appendChild(this.el("div", "vpm-row", `<div class="vpm-row-l"><b>LÍMITE DE GOLES</b><span>${g === 0 ? "∞" : g}</span></div>`));
      c.appendChild(this.el("div", "vpm-row", `<div class="vpm-row-l"><b>LÍMITE DE TIEMPO</b><span>${t === 0 ? "∞" : fmtTime(t)}</span></div>`));
      c.appendChild(this.readOnlyNote());
      return;
    }

    const goalRow = this.el("div", "vpm-row");
    goalRow.appendChild(this.el("div", "vpm-row-l", "<b>LÍMITE DE GOLES</b><span>Primer equipo en llegar gana · 0 = ilimitado</span>"));
    goalRow.appendChild(this.stepper(this.settings.goalLimit, 0, 15, 1,
      (v) => v === 0 ? "∞" : String(v),
      (v) => { this.settings.goalLimit = v; this.applyRules(); }));
    c.appendChild(goalRow);

    const timeRow = this.el("div", "vpm-row");
    timeRow.appendChild(this.el("div", "vpm-row-l", "<b>LÍMITE DE TIEMPO</b><span>Duración del partido · 0 = ilimitado</span>"));
    timeRow.appendChild(this.stepper(this.settings.timeLimit, 0, 900, 30,
      (v) => v === 0 ? "∞" : fmtTime(v),
      (v) => { this.settings.timeLimit = v; this.applyRules(); }));
    c.appendChild(timeRow);

    // Sin fila de atajos ("RÁPIDA · 3G", "ESTÁNDAR", "CRONO", "ILIMITADA"): eran
    // cuatro botones para poner dos números que están justo encima, y llenaban
    // de ruido la pantalla donde lo que importa es quién juega.
    c.appendChild(this.el("p", "vpm-hint", "Los cambios de reglas se aplican de inmediato a la partida en curso."));
  }

  private applyRules(): void {
    saveSettings(this.settings);
    if (this.onApplyRules) this.onApplyRules({ goalLimit: this.settings.goalLimit, timeLimit: this.settings.timeLimit });
  }

  private stepper(value: number, min: number, max: number, step: number, fmt: (v: number) => string, onChange: (v: number) => void): HTMLElement {
    const wrap = this.el("div", "vpm-step");
    const dec = this.el("button", "", "−");
    const val = this.el("div", "val", fmt(value));
    const inc = this.el("button", "", "+");
    let v = value;
    const set = (nv: number): void => { v = Math.max(min, Math.min(max, nv)); val.textContent = fmt(v); onChange(v); };
    dec.onclick = () => set(v - step);
    inc.onclick = () => set(v + step);
    wrap.appendChild(dec); wrap.appendChild(val); wrap.appendChild(inc);
    return wrap;
  }

  /* ---------------- MAPS ---------------- */

  /**
   * Selector de cancha.
   *
   * Las miniaturas se dibujan **a escala entre ellas** (todas con el mismo
   * px-por-unidad, sacado de la cancha más grande): antes cada una se estiraba
   * para llenar su tarjeta, así que "GRANDE" y "PEQUEÑO" se veían del mismo
   * tamaño y la única pista del cambio era un número. Encima, la cancha
   * seleccionada se muestra en grande con sus medidas reales.
   */
  private renderMaps(c: HTMLElement): void {
    const readOnly = this.onlineReadOnly;
    const current = mapById(this.settings.mapId);
    c.appendChild(this.el("p", "vpm-h", readOnly ? "Cancha de la sala" : "Cancha · cambiarla reinicia la partida"));

    // Vista grande de la cancha elegida, con la escala compartida.
    const hero = this.el("div", "vpm-map-hero tb-cut-sm");
    hero.innerHTML = mapPreviewSVG(current, 220)
      + `<div class="vpm-map-facts">`
      + `<b>${escapeHtml(current.name)}</b>`
      + `<span>${current.courtW} × ${current.courtH} px de cancha</span>`
      + `<span>Portería de ${current.goalH} px (${Math.round((current.goalH / current.courtH) * 100)}% del fondo)</span>`
      + `<span>Superficie relativa: ${relativeArea(current)}</span>`
      + `</div>`;
    c.appendChild(hero);

    const grid = this.el("div", "vpm-maps");
    for (const m of MAPS) {
      const card = this.el("div", "vpm-map tb-cut-sm" + (m.id === this.settings.mapId ? " sel" : ""));
      card.innerHTML = `<b>${escapeHtml(m.name)}</b> <span>${m.courtW}×${m.courtH}</span>`
        + `<div class="vpm-map-thumb">${mapPreviewSVG(m, 104)}</div>`
        + `<span>Portería ${m.goalH}px</span>`;
      if (readOnly) {
        card.style.cursor = "default";
        card.style.opacity = m.id === this.settings.mapId ? "1" : "0.5";
      } else {
        card.onclick = () => {
          this.settings.mapId = m.id;
          saveSettings(this.settings);
          if (this.onSelectMap) this.onSelectMap(m.id);
          this.renderPanel(this.screen === "menu");
        };
      }
      grid.appendChild(card);
    }
    c.appendChild(grid);
    if (readOnly) c.appendChild(this.readOnlyNote());
    // Estado real del editor de mapas: ni un botón que no hace nada, ni un
    // silencio. Cuando el editor exista, aquí entra la importación.
    c.appendChild(this.el("p", "vpm-hint",
      "Canchas propias: el editor de mapas y la importación llegan en una fase posterior. "
      + "Estas cuatro son las que el servidor sabe simular hoy, así que son las que se pueden jugar online."));
  }

  /* ---------------- CONFIG ---------------- */

  /**
   * Configuración del JUGADOR (no de la sala): audio, cámara, gráficos, qué se
   * muestra del partido, red y teclas.
   *
   * Antes esta pestaña se llamaba AUDIO y tenía dos controles de sonido y el
   * zoom, mientras las teclas vivían escondidas en ACCESO. Ahora ACCESO es solo
   * accesibilidad (ver `renderAccess`) y todo lo que es "cómo quiero que se
   * comporte el juego" está aquí, en el orden en que se busca.
   */
  private renderConfig(c: HTMLElement): void {
    /* --- audio --- */
    c.appendChild(this.el("p", "vpm-h", "Audio"));
    const volLabel = this.el("div", "vpm-row-l", `<b>VOLUMEN MAESTRO · ${Math.round(this.settings.volume * 100)}%</b>`);
    c.appendChild(volLabel);
    const vol = this.el("input", "tb-range");
    vol.type = "range"; vol.min = "0"; vol.max = "100"; vol.value = String(Math.round(this.settings.volume * 100));
    vol.oninput = () => {
      this.settings.volume = Number(vol.value) / 100;
      volLabel.innerHTML = `<b>VOLUMEN MAESTRO · ${vol.value}%</b>`;
      this.emitSettings();
    };
    c.appendChild(vol);
    c.appendChild(this.toggle(
      this.settings.muted ? "SILENCIADO" : "SONIDO ACTIVO",
      !this.settings.muted,
      (on) => { this.settings.muted = !on; },
    ));

    /* --- cámara --- */
    c.appendChild(this.el("p", "vpm-h", "Cámara"));
    c.appendChild(this.segmented(
      ["LEJANO", "MEDIO", "CERCA"].map((label, i) => [i, label] as [number, string]),
      this.settings.zoom,
      (v) => { this.settings.zoom = v; },
    ));
    c.appendChild(this.el("p", "vpm-hint", "También se cambia en partida con las teclas 1, 2 y 3."));

    /* --- gráficos --- */
    c.appendChild(this.el("p", "vpm-h", "Gráficos"));
    c.appendChild(this.el("div", "vpm-row-l", "<b>RESOLUCIÓN DE RENDER</b>"));
    c.appendChild(this.segmented<Quality>(
      [["auto", "AUTO"], ["high", "MÁXIMA"], ["low", "RENDIMIENTO"]],
      this.settings.quality,
      (v) => { this.settings.quality = v; },
    ));
    c.appendChild(this.el("p", "vpm-hint",
      "AUTO baja la resolución sola si los fps caen y la recupera al mejorar. "
      + "MÁXIMA la fija (imagen nítida aunque se note el esfuerzo); RENDIMIENTO la baja siempre."));
    c.appendChild(this.toggle("EFECTOS DE PATADA Y PARTÍCULAS", this.settings.effects, (on) => { this.settings.effects = on; }));
    c.appendChild(this.toggle("ESTELAS DE MOVIMIENTO", this.settings.trails, (on) => { this.settings.trails = on; }));

    /* --- lo que se ve del partido --- */
    c.appendChild(this.el("p", "vpm-h", "Información en pantalla"));
    c.appendChild(this.toggle("NOMBRES SOBRE LOS DISCOS", this.settings.showNames, (on) => { this.settings.showNames = on; }));
    c.appendChild(this.toggle("CONTADOR DE FPS", this.settings.showFps, (on) => { this.settings.showFps = on; }));
    c.appendChild(this.toggle("GRÁFICO DE PING", this.settings.showPing, (on) => { this.settings.showPing = on; }));

    /* --- red --- */
    c.appendChild(this.el("p", "vpm-h", "Red · extrapolación"));
    c.appendChild(this.segmented<Extrapolation>(
      [["smooth", "SUAVE"], ["auto", "AUTO"], ["sharp", "INMEDIATO"]],
      this.settings.extrapolation,
      (v) => { this.settings.extrapolation = v; },
    ));
    c.appendChild(this.el("p", "vpm-hint",
      "Cuánto se dibuja a los rivales en el pasado. SUAVE añade colchón (movimiento limpio, "
      + "un pelo de retraso); INMEDIATO los extrapola al presente (responde antes, con alguna "
      + "corrección visible); AUTO lo decide según el jitter medido de tu conexión."));

    /* --- controles --- */
    c.appendChild(this.el("p", "vpm-h", "Mando táctil"));
    c.appendChild(this.segmented<TouchMode>(
      [["auto", "AUTO"], ["on", "SIEMPRE"], ["off", "NUNCA"]],
      this.settings.touchControls,
      (v) => { this.settings.touchControls = v; },
    ));
    c.appendChild(this.el("p", "vpm-hint", "AUTO lo enseña solo en dispositivos táctiles."));

    c.appendChild(this.el("p", "vpm-h", "Teclas"));
    c.appendChild(this.renderKeybinds());

    /* --- reportar --- */
    // También desde aquí, porque los fallos se ven JUGANDO: obligar a salir al
    // menú principal para contarlos es garantizar que no se cuenten.
    c.appendChild(this.el("p", "vpm-h", "Feedback"));
    const fb = this.el("button", "tb-btn tb-cut-sm", "💬 MANDAR FEEDBACK");
    fb.onclick = () => this.renderFeedback();
    c.appendChild(fb);
    c.appendChild(this.el("p", "vpm-hint",
      "Fallos, ideas y opiniones. Con los datos técnicos que decidas adjuntar, y nada más."));
  }

  /** Interruptor de una opción booleana (guarda y repinta al cambiar). */
  private toggle(label: string, on: boolean, set: (v: boolean) => void): HTMLElement {
    const t = this.el("div", "tb-switch" + (on ? " on" : ""));
    t.innerHTML = `<i></i><span>${escapeHtml(label)}</span>`;
    t.onclick = () => {
      set(!on);
      this.emitSettings();
      this.renderPanel(this.screen === "menu");
    };
    return t;
  }

  /** Selector segmentado de una lista cerrada de valores. */
  private segmented<T extends string | number>(
    options: [T, string][], value: T, set: (v: T) => void,
  ): HTMLElement {
    const seg = this.el("div", "tb-seg");
    for (const [v, label] of options) {
      const b = this.el("button", "tb-btn tb-cut-sm" + (v === value ? " accent" : " ghost"), label);
      b.onclick = () => {
        set(v);
        this.emitSettings();
        this.renderPanel(this.screen === "menu");
      };
      seg.appendChild(b);
    }
    return seg;
  }

  /** Deslizador de un ajuste numérico, con su valor en la etiqueta. */
  private slider(
    label: string, value: number, min: number, max: number, step: number,
    fmt: (v: number) => string, set: (v: number) => void,
  ): HTMLElement {
    const wrap = this.el("div", "vpm-slider-row");
    const cap = this.el("div", "vpm-row-l", `<b>${escapeHtml(label)} · ${fmt(value)}</b>`);
    wrap.appendChild(cap);
    const input = this.el("input", "tb-range") as HTMLInputElement;
    input.type = "range";
    input.min = String(min);
    input.max = String(max);
    input.step = String(step);
    input.value = String(value);
    input.oninput = () => {
      const v = Number(input.value);
      cap.innerHTML = `<b>${escapeHtml(label)} · ${fmt(v)}</b>`;
      set(v);
      this.emitSettings(); // en vivo: se ve el efecto mientras se arrastra
    };
    wrap.appendChild(input);
    return wrap;
  }

  private emitSettings(): void {
    saveSettings(this.settings);
    // El filtro de color y la escala de texto se aplican AQUÍ además de en el
    // consumidor: quien está moviendo el deslizador necesita ver el efecto en
    // la propia pantalla de ajustes para saber si le sirve.
    applyVision(this.settings);
    applyUiScale(this.settings.uiScale);
    if (this.onSettingsChange) this.onSettingsChange(this.settings);
  }

  /* ---------------- ACCESO ---------------- */

  /**
   * Accesibilidad. Global (no de la sala) porque describe a la PERSONA que
   * juega, no a la partida.
   *
   * Aquí NO se eligen los colores de los equipos. Eso resolvía el caso de dos
   * discos de colores y se rompía en cuanto el juego tiene poleras por equipo,
   * estelas de patada o explosiones de gol: habría que elegir el color de cada
   * cosa. Lo que se ajusta es un FILTRO sobre toda la imagen (ver `a11y.ts`),
   * que corrige lo que se dibuje hoy y lo que se dibuje mañana.
   */
  private renderAccess(c: HTMLElement): void {
    const s = this.settings;

    c.appendChild(this.el("p", "vpm-h", "Filtro de color"));
    c.appendChild(this.el("p", "vpm-hint",
      "Se aplica a TODA la imagen (discos, poleras, efectos de patada, explosiones de gol "
      + "y menús), no a colores sueltos. Redistribuye el color que no se distingue hacia "
      + "los ejes que sí, en vez de repintar el juego."));

    const filters: [ColorFilter, string, string][] = [
      ["none", "SIN FILTRO", "Colores originales"],
      ["deuteranopia", "DEUTERANOPIA", "Dificultad con el verde"],
      ["protanopia", "PROTANOPIA", "Dificultad con el rojo"],
      ["tritanopia", "TRITANOPIA", "Dificultad con el azul"],
    ];
    const list = this.el("div", "vpm-a11y-modes");
    for (const [filter, label, help] of filters) {
      const row = this.el("button", "vpm-a11y-mode tb-cut-sm" + (s.colorFilter === filter ? " on" : ""));
      // Cada opción lleva una muestra con los colores REALES del juego, para
      // poder comparar de un vistazo antes de decidir.
      row.innerHTML =
        `<span class="sw" style="background:${TEAM_COLORS[0]}"></span>`
        + `<span class="sw" style="background:${TEAM_COLORS[1]}"></span>`
        + `<span class="lb">${label}<small>${help}</small></span>`;
      row.onclick = () => {
        s.colorFilter = filter;
        this.emitSettings();
        this.renderPanel(this.screen === "menu");
      };
      list.appendChild(row);
    }
    c.appendChild(list);

    if (s.colorFilter !== "none") {
      c.appendChild(this.slider(
        "INTENSIDAD DEL FILTRO", Math.round(s.filterStrength * 100), 0, 100, 5,
        (v) => `${v}%`,
        (v) => { s.filterStrength = clampStrength(v / 100); },
      ));
      c.appendChild(this.el("p", "vpm-hint",
        "Al 100% la corrección es completa; bajarla deja los colores más naturales. "
        + "La dicromacia no es un interruptor: elige lo que TE deje distinguir los equipos."));
    }

    /* --- contraste y saturación (baja visión, pantallas malas, sol) --- */
    c.appendChild(this.el("p", "vpm-h", "Contraste y saturación"));
    c.appendChild(this.slider(
      "CONTRASTE", Math.round(s.contrast * 100), Math.round(MIN_CONTRAST * 100), Math.round(MAX_CONTRAST * 100), 5,
      (v) => `${v}%`,
      (v) => { s.contrast = clampContrast(v / 100); },
    ));
    c.appendChild(this.slider(
      "SATURACIÓN", Math.round(s.saturation * 100), Math.round(MIN_SATURATION * 100), Math.round(MAX_SATURATION * 100), 5,
      (v) => `${v}%`,
      (v) => { s.saturation = clampSaturation(v / 100); },
    ));
    c.appendChild(this.el("p", "vpm-hint",
      "Saturación a 0% deja la imagen en escala de grises: es la prueba honesta de si el "
      + "juego se puede seguir sin depender del color."));

    /* --- lo que no depende del color --- */
    c.appendChild(this.el("p", "vpm-h", "Sin depender del color"));
    c.appendChild(this.toggle("MARCA DE FORMA POR EQUIPO", s.teamMarkers, (on) => { s.teamMarkers = on; }));
    c.appendChild(this.el("p", "vpm-hint",
      "Anillo continuo para un equipo y partido para el otro. Es lo único que sigue "
      + "funcionando en escala de grises, así que viene activado."));
    c.appendChild(this.toggle("REDUCIR DESTELLOS", s.reduceFlashes, (on) => { s.reduceFlashes = on; }));
    c.appendChild(this.el("p", "vpm-hint",
      "Suaviza el fogonazo de gol y los flashes a pantalla completa (fotosensibilidad)."));

    /* --- tamaño de texto --- */
    c.appendChild(this.el("p", "vpm-h", "Tamaño de texto"));
    c.appendChild(this.slider(
      "TEXTO DE LA INTERFAZ", Math.round(s.uiScale * 100),
      Math.round(MIN_UI_SCALE * 100), Math.round(MAX_UI_SCALE * 100), 5,
      (v) => `${v}%`,
      (v) => { s.uiScale = clampUiScale(v / 100); applyUiScale(s.uiScale); },
    ));
  }

  /**
   * Lista de acciones con sus teclas. Pulsar una fila entra en "escuchando" y la
   * siguiente tecla queda asignada.
   *
   * Se guarda el CARÁCTER de la tecla, no su posición física: en un teclado
   * AZERTY las teclas donde el WASD inglés cae se leen ZQSD, así que guardar la
   * posición ataría a cada jugador a la disposición de otro (ver `keybinds.ts`).
   */
  private renderKeybinds(): HTMLElement {
    const wrap = this.el("div", "vpm-keys");
    /** Acción en espera de tecla (null = ninguna). */
    let listening: Action | null = null;
    let onKey: ((e: KeyboardEvent) => void) | null = null;

    const stopListening = (): void => {
      if (onKey) window.removeEventListener("keydown", onKey, true);
      onKey = null;
      listening = null;
    };

    const draw = (): void => {
      wrap.innerHTML = "";
      for (const action of ACTIONS) {
        const row = this.el("div", "vpm-key-row");
        row.appendChild(this.el("span", "k-name", escapeHtml(ACTION_LABELS[action])));
        const keys = this.settings.keybinds[action] ?? [];
        const btn = this.el(
          "button",
          "tb-bevel k-btn" + (listening === action ? " listening" : ""),
          listening === action
            ? "PULSA UNA TECLA…"
            : keys.map((k) => escapeHtml(keyLabel(k))).join(" · "),
        );
        btn.onclick = () => {
          stopListening();
          listening = action;
          draw();
          onKey = (e: KeyboardEvent): void => {
            // Se captura en fase de captura y se corta: mientras se está
            // reasignando, la tecla no debe además mover el disco ni abrir el chat.
            e.preventDefault();
            e.stopPropagation();
            if (e.key === "Escape") { stopListening(); draw(); return; }
            this.settings.keybinds = assignKey(this.settings.keybinds, action, normalizeKey(e.key));
            stopListening();
            this.emitSettings();
            draw();
          };
          window.addEventListener("keydown", onKey, true);
        };
        row.appendChild(btn);
        wrap.appendChild(row);
      }
      const reset = this.el("button", "tb-btn tb-cut-sm ghost", "RESTAURAR TECLAS POR DEFECTO");
      reset.onclick = () => {
        stopListening();
        this.settings.keybinds = defaultBindings();
        this.emitSettings();
        draw();
      };
      wrap.appendChild(reset);
    };

    draw();
    return wrap;
  }

  /* ---------------- CHAT ---------------- */

  /**
   * Vista de chat completa (registro + barra de escritura). La usan las DOS
   * apariciones del chat: la pestaña CHAT y la columna lateral permanente de
   * escritorio. Es la misma conversación, así que es el mismo componente — dos
   * implementaciones acabarían divergiendo a la primera.
   */
  private chatView(autofocus: boolean): HTMLElement {
    const wrap = this.el("div", "vpm-chat");
    const log = this.el("div", "vpm-log tb-scroll");
    for (const m of this.chat) log.appendChild(this.chatLine(m));
    wrap.appendChild(log);

    const bar = this.el("div", "vpm-chatbar");
    const input = this.el("input", "tb-input");
    input.placeholder = "Mensaje…  (/r · /b para cambiar de equipo)";
    input.maxLength = MAX_CHAT;
    const send = (): void => {
      const t = input.value.trim();
      if (!t) return;
      input.value = "";
      this.handleChatInput(t);
    };
    input.onkeydown = (e) => { if (e.key === "Enter") send(); e.stopPropagation(); };
    const btn = this.el("button", "tb-btn tb-cut-sm accent", "▶");
    btn.title = "Enviar";
    btn.onclick = send;
    bar.appendChild(input);
    bar.appendChild(btn);
    wrap.appendChild(bar);
    setTimeout(() => { log.scrollTop = log.scrollHeight; if (autofocus) input.focus(); }, 0);
    return wrap;
  }

  private renderChat(c: HTMLElement): void {
    c.appendChild(this.chatView(true));
  }

  /** Columna de chat de escritorio (la enseña el CSS a partir de 80rem). */
  private renderSideChat(): HTMLElement {
    const side = this.el("div", "vpm-side");
    const head = this.el("p", "vpm-h", "Sala");
    side.appendChild(head);
    side.appendChild(this.chatView(false));
    return side;
  }

  /**
   * Una línea del chat del panel de pausa.
   *
   * `who` y `text` se escapan SIEMPRE. Antes no hacía falta pensarlo: el único
   * autor posible era el jugador local y como mucho se hacía un destrozo a sí
   * mismo. Con el chat en red el nombre lo elige OTRA persona, la moderación
   * (SEC-2) filtra insultos pero no marcado — deja pasar `<` y `>` — y esto se
   * inyecta como HTML. Sin escapar, un nombre es un script que se ejecuta en la
   * pantalla de todos los de la sala.
   */
  private chatLine(m: ChatMsg): HTMLElement {
    if (m.sys) return this.el("div", "vpm-msg sys", `» ${escapeHtml(m.text)}`);
    const tc = m.team === 0 ? "red" : m.team === 1 ? "blue" : "";
    return this.el("div", "vpm-msg", `<span class="who ${tc}">${escapeHtml(m.who)}:</span> ${escapeHtml(m.text)}`);
  }

  private handleChatInput(text: string): void {
    const me = this.roster.find((r) => r.local);
    if (text === "/r" || text === "/b") {
      const team = text === "/r" ? 0 : 1;
      // Online el equipo lo decide el SERVIDOR (y solo si tienes permiso): se
      // pide y se espera al `peers` que vuelva. Moverlo aquí pintaría un cambio
      // que quizá el servidor rechaza, y la pantalla mentiría hasta el siguiente
      // roster.
      const info = this.onlinePeers;
      if (info) this.onSetTeam?.(info.localSlot, team);
      else this.assignTeam(me?.id ?? null, team);
      return;
    }
    // SEC-2: el mismo filtro que aplica el servidor, aquí solo para dar respuesta
    // inmediata a quien escribe. El veredicto que cuenta sigue siendo el suyo.
    const verdict = moderateChat(text);
    if (verdict.blocked) return;
    // Si salió al servidor, lo pintará el eco (ver `onChatSend`). Y se le pone
    // un plazo: sin eco, el mensaje se perdió y hay que decirlo.
    if (this.onChatSend?.(verdict.text) === true) { this.trackPending(verdict.text); return; }
    this.pushChat(verdict.text, me ? me.team : null, false, me ? me.name : "TÚ");
  }

  /**
   * Pinta un mensaje de chat difundido por la sala. El emisor (nombre, equipo)
   * lo determinó el servidor desde su roster, no el cliente que lo escribió.
   *
   * SEC-3 · El filtro de silenciados se aplica AQUÍ, en recepción, y no en el
   * servidor: si el servidor dejara de entregar los mensajes, el silenciado
   * podría deducir quién le silenció por cómo se comporta el chat, y silenciar
   * pasaría a ser un gesto público. Filtrando al recibir, no se entera nadie.
   */
  pushRemoteChat(line: { slot: number; name: string; team?: 0 | 1 | "spec"; text: string }): void {
    // El eco de un mensaje PROPIO cierra su espera: llegó a la sala.
    if (line.slot === this.onlinePeers?.localSlot) this.resolvePending(line.text);
    const peer = this.onlinePeers?.peers.find((p) => p.slot === line.slot);
    if (this.muted.isMuted(peer?.muteId)) return;
    const team: Team = line.team === 0 || line.team === 1 ? line.team : null;
    this.pushChat(line.text, team, false, line.name);
  }

  /**
   * Fija a qué partida pertenece el chat que se muestra.
   *
   * Cambiar de ámbito vacía el registro: la conversación de una sala no es de
   * quien la escuchó, es de la sala. Repetir el mismo ámbito no hace nada, así
   * que reconectar a la misma sala conserva el hilo.
   */
  setChatScope(scope: string): void {
    if (scope === this.chatScope) return;
    this.chatScope = scope;
    this.chat = welcomeChat();
    this.clearPending();
    // Las dos vistas montadas (pestaña CHAT y columna lateral) y el chat sobre
    // la cancha se vacían con él: si no, seguirían enseñando lo de la sala vieja.
    for (const log of this.root.querySelectorAll<HTMLElement>(".vpm-log")) {
      log.innerHTML = "";
      for (const m of this.chat) log.appendChild(this.chatLine(m));
    }
    if (this.igLog) this.igLog.innerHTML = "";
  }

  /** Un mensaje propio salió al servidor: se espera su eco antes de darlo por vivo. */
  private trackPending(text: string): void {
    const entry = {
      text,
      timer: setTimeout(() => {
        this.pendingChat = this.pendingChat.filter((p) => p !== entry);
        // Se dice, y se dice ENTERO: quien escribió tiene que poder recuperar su
        // texto sin volver a teclearlo de memoria.
        this.pushChat(`No se pudo enviar «${text}». Estás escribiendo muy seguido: espera un momento y reinténtalo.`, null, true);
      }, CHAT_ECHO_TIMEOUT_MS),
    };
    this.pendingChat.push(entry);
  }

  /** Llegó el eco de un mensaje propio: cancela su aviso de "no se pudo enviar". */
  private resolvePending(text: string): void {
    const i = this.pendingChat.findIndex((p) => p.text === text);
    // El servidor puede haber enmascarado insultos, así que el texto no siempre
    // vuelve igual: si no hay coincidencia exacta, se resuelve el más antiguo.
    const idx = i >= 0 ? i : (this.pendingChat.length ? 0 : -1);
    if (idx < 0) return;
    clearTimeout(this.pendingChat[idx].timer);
    this.pendingChat.splice(idx, 1);
  }

  private clearPending(): void {
    for (const p of this.pendingChat) clearTimeout(p.timer);
    this.pendingChat = [];
  }

  /**
   * SEC-3 · Diálogo de reporte. Motivos cerrados y sin campo libre: un texto
   * libre sería un canal más sin moderar, y para revisar un caso el motivo y la
   * sala bastan.
   */
  private openReport(peer: PeerInfo): void {
    const reasons: [ReportReason, string][] = [
      ["cheating", "Trampas"],
      ["abuse", "Insultos o acoso"],
      ["spam", "Spam"],
      ["other", "Otro"],
    ];
    const back = this.el("div", "vpm-modal");
    const card = this.el("div", "vpm-modal-card tb-cut tb-edge");
    card.appendChild(this.el("p", "vpm-h", `Reportar a ${escapeHtml(peer.name)}`));
    for (const [reason, label] of reasons) {
      const b = this.el("button", "tb-btn tb-cut-sm", label.toUpperCase());
      b.onclick = () => {
        this.onReport?.(peer.slot, reason);
        back.remove();
        // Confirmación local. El servidor no acusa recibo a propósito: contestar
        // convertiría el reporte en algo observable desde fuera.
        this.pushChat("Reporte enviado. Gracias: lo revisaremos.", null, true);
      };
      card.appendChild(b);
    }
    const cancel = this.el("button", "tb-btn tb-cut-sm ghost", "CANCELAR");
    cancel.onclick = () => back.remove();
    card.appendChild(cancel);
    back.appendChild(card);
    back.onclick = (e) => { if (e.target === back) back.remove(); };
    document.body.appendChild(back);
  }

  /** API pública para que el juego empuje mensajes de sistema (goles, etc.). */
  pushChat(text: string, team: Team = null, sys = true, who = ""): void {
    const msg: ChatMsg = { who, text, team, sys };
    this.chat.push(msg);
    if (this.chat.length > 80) this.chat.shift();
    this.appendIgLine(msg);
    // El chat del panel se rellenaba solo al enviar. Con el chat en red los
    // mensajes llegan solos, así que las ventanas abiertas tienen que crecer
    // con ellos — si no, quien tiene el panel abierto no ve llegar nada.
    //
    // En plural: en escritorio hay DOS registros montados a la vez (la pestaña
    // CHAT y la columna lateral). Se escribe en todos los que estén en el
    // documento, que es lo que garantiza que muestren la misma conversación.
    for (const log of this.root.querySelectorAll<HTMLElement>(".vpm-log")) {
      log.appendChild(this.chatLine(msg));
      log.scrollTop = log.scrollHeight;
    }
  }

  /* ---------------- RESULT ---------------- */

  /**
   * Nombres de cada bando para la pantalla de fin: los del EQUIPO (los mismos
   * del marcador, para que el juego hable un solo idioma) y, debajo, quién
   * jugaba en cada uno.
   *
   * Si algún bando se queda sin jugadores (partida local de práctica, rival que
   * se fue) no se listan por un lado sí y por otro no: enfrentar "Ana" contra
   * nada se lee como un error de la interfaz.
   */
  private sideNames(): [string, string] {
    return [this.teamNames[0], this.teamNames[1]];
  }

  /** Jugadores de cada bando, o cadena vacía si no hay lista que enseñar. */
  private sidePlayers(): [string, string] {
    const info = this.onlinePeers;
    const names = info
      ? (t: 0 | 1): string => info.peers.filter((p) => p.team === t).map((p) => p.name).join(" · ")
      : (t: 0 | 1): string => this.roster.filter((r) => r.team === t).map((r) => r.name).join(" · ");
    const red = names(0);
    const blue = names(1);
    return red && blue ? [red, blue] : ["", ""];
  }

  /**
   * UX-1 · Pantalla de fin de partida.
   *
   * Cierra el bucle de la partida: quién ganó, con qué marcador, contra quién y
   * cuánto duró — y las dos únicas salidas que importan, **revancha** o menú.
   *
   * En ONLINE la revancha la ordena el servidor (`restart` es una acción de
   * admin). Por eso al invitado se le muestra el botón deshabilitado con el
   * motivo, en vez de un botón que al pulsarlo no hace nada: un control que
   * calla se lee como un juego roto.
   */
  /**
   * Pantalla de fin de partido.
   *
   * Dura: la secuencia de celebración va por CSS (~2,5 s de efectos, botones a
   * 1,5 s) y el panel se queda hasta que alguien decide qué hacer. Además queda
   * BLOQUEADO durante `RESULT_LOCK_MS`: online, el servidor cambia la fase a
   * "over" justo después del gol y ese cambio cerraba el menú solo — quien
   * acababa de ganar veía su victoria medio segundo y volvía a la cancha.
   */
  showResult(winner: Team, score: [number, number]): void {
    this.paused = true;
    this.resultShowing = true;
    this.resultLockUntil = Date.now() + RESULT_LOCK_MS;
    this.root.classList.add("open");
    this.root.innerHTML = "";
    const panel = this.el("div", "vpm-panel tb-panel tb-cut tb-edge");
    const body = this.el("div", "vpm-content vpm-result");
    body.style.display = "flex";
    body.style.flexDirection = "column";
    body.style.justifyContent = "center";

    const meTeam = this.getLocalTeam() ?? 0;
    let cls = "draw", label = "EMPATE";
    if (winner !== null) {
      if (winner === meTeam) { cls = "win"; label = "VICTORIA"; }
      else { cls = "lose"; label = "DERROTA"; }
    }
    body.classList.add(cls);
    const [redName, blueName] = this.sideNames();

    // Efectos: destello, tres ondas y un abanico de chispas. Puro CSS.
    body.appendChild(this.el("div", "vpm-res-flash"));
    for (let i = 0; i < 3; i++) body.appendChild(this.el("div", "vpm-res-ring"));
    const SPARKS = 14;
    for (let i = 0; i < SPARKS; i++) {
      const sp = this.el("div", "vpm-res-spark");
      // Abanico completo con una caída ligera: los ángulos se reparten por igual
      // y la distancia varía, para que no se lea como un reloj.
      const ang = (i / SPARKS) * Math.PI * 2;
      const dist = 8 + (i % 4) * 3.2;
      sp.style.setProperty("--dx", `${(Math.cos(ang) * dist).toFixed(2)}rem`);
      sp.style.setProperty("--dy", `${(Math.sin(ang) * dist + 2).toFixed(2)}rem`);
      sp.style.animationDelay = `${(i * 0.045).toFixed(3)}s`;
      body.appendChild(sp);
    }

    body.appendChild(this.el("div", "vpm-res-sub", "FIN DEL PARTIDO"));
    body.appendChild(this.el("h1", cls, label));
    body.appendChild(this.el("div", "big", `<span class="red">${score[0]}</span> : <span class="blue">${score[1]}</span>`));
    body.appendChild(this.el("div", "vpm-res-sides",
      `<div class="vpm-res-side red">${escapeHtml(redName)}</div>` +
      `<div class="vpm-res-side blue">${escapeHtml(blueName)}</div>`));
    const [redPlayers, bluePlayers] = this.sidePlayers();
    if (redPlayers || bluePlayers) {
      body.appendChild(this.el("div", "vpm-res-sides vpm-res-players",
        `<div class="vpm-res-side">${escapeHtml(redPlayers)}</div>` +
        `<div class="vpm-res-side">${escapeHtml(bluePlayers)}</div>`));
    }

    // Resumen: duración y por qué terminó. Da contexto sin pedir telemetría nueva.
    const m = this.getMatch?.() ?? null;
    if (m) {
      const total = score[0] + score[1];
      const byGoals = m.goalLimit > 0 && Math.max(score[0], score[1]) >= m.goalLimit;
      const reason = byGoals ? `${m.goalLimit} GOLES` : m.timeLimit > 0 ? "TIEMPO" : "FINALIZADO";
      body.appendChild(this.el("div", "vpm-res-stats",
        `<div class="vpm-res-stat"><b>${fmtTime(m.matchTime)}</b><span>DURACIÓN</span></div>` +
        `<div class="vpm-res-stat"><b>${total}</b><span>GOLES</span></div>` +
        `<div class="vpm-res-stat"><b>${reason}</b><span>FINAL POR</span></div>`));
    }

    const foot = this.el("div", "vpm-foot tb-foot vpm-res-foot");
    // Online: solo el admin puede reiniciar la sala (lo impone el servidor).
    const needsAdmin = this.onlinePeers !== null && !this.localIsAdmin;
    const again = this.el("button", "tb-btn tb-cut-sm primary", "⟳ REVANCHA");
    if (needsAdmin) {
      again.disabled = true;
      again.title = "Solo el anfitrión puede iniciar la revancha";
    } else {
      again.onclick = () => { this.resultShowing = false; this.paused = false; this.hide(); this.onRestart?.(); };
    }
    const menu = this.el("button", "tb-btn tb-cut-sm ghost", "✕ MENÚ PRINCIPAL");
    menu.onclick = () => this.toMenu();
    foot.appendChild(again);
    foot.appendChild(menu);
    panel.appendChild(body);
    if (needsAdmin) body.appendChild(this.el("p", "vpm-hint", "Esperando a que el anfitrión inicie la revancha…"));
    panel.appendChild(foot);
    this.root.appendChild(panel);
  }

  /** Cierra la pantalla de fin de partida (p. ej. el servidor arrancó la revancha). */
  /**
   * Baja la pantalla de resultado (empezó otra partida).
   *
   * Respeta el bloqueo: si la revancha arranca a los dos segundos, el resultado
   * del partido anterior no puede desaparecer a media celebración. Devuelve
   * `false` si todavía no tocaba, para que quien llama lo reintente.
   */
  hideResult(): boolean {
    if (!this.paused) return true;
    if (this.resultShowing && Date.now() < this.resultLockUntil) return false;
    this.resultShowing = false;
    this.paused = false;
    this.hide();
    return true;
  }

  /** ¿Está la pantalla de resultado en su ventana intocable? */
  get resultLocked(): boolean {
    return this.resultShowing && Date.now() < this.resultLockUntil;
  }

  /* ---------------- IN-GAME CHAT (estilo LoL) ---------------- */
  isTyping(): boolean { return this.typing; }

  /**
   * Abre el compositor de chat desde el botón táctil.
   *
   * En móvil no hay tecla Intro, así que hasta ahora escribir obligaba a abrir
   * el menú de pausa — es decir, a parar de jugar para decir "voy". El botón de
   * la esquina hace lo mismo que Intro en el teclado.
   */
  openChatFromTouch(): void {
    if (this.screen !== "playing" || this.paused || this.typing) return;
    this.openIgChat();
  }

  private mountInGameChat(): void {
    if (this.ig) return;
    this.ig = this.el("div", "vpm-ig");
    this.igLog = this.el("div", "vpm-ig-log");
    this.igInput = this.el("input", "vpm-ig-input");
    this.igInput.maxLength = MAX_CHAT;
    this.igInput.placeholder = "Mensaje…  (/r ataque · /b defensa · Esc cancela)";
    this.igInput.onkeydown = (e) => {
      e.stopPropagation();
      if (e.key === "Enter") this.closeIgChat(true);
      else if (e.key === "Escape") this.closeIgChat(false);
    };
    /**
     * Perder el foco CIERRA la composición.
     *
     * Sin esto se llegaba a un estado sin salida: bastaba con hacer clic en la
     * cancha (o que otra cosa robara el foco) para quedarse "escribiendo" sin
     * cursor — las teclas no movían el disco, porque la UI se creía en el chat,
     * y Enter no reabría nada, porque ya estaba abierto. El síntoma que llega es
     * "de repente no puedo enviar mensajes"; en realidad tampoco se podía jugar.
     * Lo tecleado se guarda y vuelve al reabrir: cerrar no es tirar el borrador.
     */
    this.igInput.onblur = () => {
      if (!this.typing) return;
      this.igDraft = this.igInput?.value ?? "";
      this.closeIgChat(false);
    };
    this.ig.appendChild(this.igLog);
    this.ig.appendChild(this.igInput);
    document.body.appendChild(this.ig);

    // Poblar con el historial reciente
    for (const m of this.chat.slice(-6)) this.appendIgLine(m, true);

    // Enter abre el chat cuando se está jugando (sin pausa ni escribiendo).
    // Guard: si el foco está en OTRO campo de texto (p. ej. la pantalla de nombre
    // o el lobby recién cerrados), ignora el Enter — así el invitado no arranca
    // "atrapado" en el chat y puede moverse a la primera (TAREA B).
    this.igKeyHandler = (e: KeyboardEvent) => {
      if (e.key !== "Enter" || this.screen !== "playing" || this.paused || this.typing) return;
      const ae = document.activeElement;
      if (ae && ae !== this.igInput && (ae.tagName === "INPUT" || ae.tagName === "TEXTAREA")) return;
      e.preventDefault();
      this.openIgChat();
    };
    window.addEventListener("keydown", this.igKeyHandler);
  }

  private openIgChat(): void {
    if (!this.ig || !this.igInput) return;
    this.typing = true;
    this.ig.classList.add("typing");
    this.igInput.value = this.igDraft;
    this.igDraft = "";
    if (this.igLog) this.igLog.scrollTop = this.igLog.scrollHeight;
    setTimeout(() => this.igInput?.focus(), 0);
    if (this.onTypingChange) this.onTypingChange(true);
  }

  private closeIgChat(send: boolean): void {
    if (!this.ig || !this.igInput) return;
    const text = this.igInput.value.trim();
    if (send) this.igDraft = ""; // se envía: no queda borrador que recuperar
    this.typing = false;
    this.ig.classList.remove("typing");
    this.igInput.blur();
    this.igInput.value = "";
    if (send && text) this.handleChatInput(text);
    if (this.onTypingChange) this.onTypingChange(false);
  }

  private appendIgLine(m: ChatMsg, instant = false): void {
    if (!this.igLog) return;
    const line = this.chatLineIg(m);
    if (instant) line.style.animationDelay = "0s";
    this.igLog.appendChild(line);
    while (this.igLog.childElementCount > 24) this.igLog.firstElementChild?.remove();
    this.igLog.scrollTop = this.igLog.scrollHeight;
  }

  private chatLineIg(m: ChatMsg): HTMLElement {
    if (m.sys) return this.el("div", "vpm-ig-line sys", `» ${escapeHtml(m.text)}`);
    const tc = m.team === 0 ? "red" : m.team === 1 ? "blue" : "";
    return this.el("div", "vpm-ig-line", `<span class="who ${tc}">${escapeHtml(m.who)}</span> ${escapeHtml(m.text)}`);
  }

  /* ---------------- HUD pause button ---------------- */
  /**
   * Botón flotante de FEEDBACK, abajo a la derecha y siempre a mano.
   *
   * Se monta una vez y vive en `document.body`, fuera del panel: así sigue ahí
   * aunque la UI se repinte entera. Lo que decide si se ve es `syncFab`.
   */
  private mountFab(): void {
    if (this.fab) return;
    this.fab = this.el("button", "tb-bevel vpm-fab", "💬 FEEDBACK");
    this.fab.title = "Contarnos un fallo, una idea o una opinión";
    this.fab.onclick = () => { this.renderFeedback(); this.syncFab(); };
    document.body.appendChild(this.fab);
    this.syncFab();
  }

  /**
   * ¿Se ve el botón flotante ahora mismo?
   *
   * Está oculto con cualquier panel abierto (perfil, ajustes, pausa: ahí dentro
   * ya hay una entrada a FEEDBACK y flotando encima solo estorbaría) y, sobre
   * todo, EN PARTIDA. Un botón flotando sobre la cancha es un botón que se pulsa
   * sin querer en mitad de una jugada, y encima ocupa la esquina donde vive el
   * medidor de ping. Jugando no se reporta nada: se juega. Desde el menú de
   * pausa → CONFIG se llega igual sin salir de la sala.
   */
  private syncFab(): void {
    if (!this.fab) return;
    const panelAbierto = this.panelOpen || this.overlayScreen !== "none";
    const visible = !panelAbierto && this.screen !== "playing";
    this.fab.classList.toggle("show", visible);
  }

  private mountPauseBtn(): void {
    this.mountInGameChat();
    if (this.pauseBtn) return;
    this.pauseBtn = this.el("button", "tb-bevel vpm-pausebtn", "❚❚");
    this.pauseBtn.title = "Pausa (Esc)";
    this.pauseBtn.onclick = () => this.openPause();
    document.body.appendChild(this.pauseBtn);
  }

  removePauseBtn(): void {
    if (this.pauseBtn) { this.pauseBtn.remove(); this.pauseBtn = null; }
    if (this.igKeyHandler) { window.removeEventListener("keydown", this.igKeyHandler); this.igKeyHandler = null; }
    if (this.ig) { this.ig.remove(); this.ig = null; this.igLog = null; this.igInput = null; }
    // Salir con el chat abierto tiene que APAGAR los puntitos: si no, el último
    // estado que la sala recibió de este jugador es "escribiendo", y ahí se
    // queda mientras el servidor recuerde su sitio.
    const wasTyping = this.typing;
    this.typing = false;
    this.igDraft = "";
    this.clearPending();
    if (wasTyping) this.onTypingChange?.(false);
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[ch] as string));
}

/** Cancha más grande del catálogo: fija la escala común de las vistas previas. */
const MAX_COURT_W = Math.max(...MAPS.map((m) => m.courtW));
const MAX_COURT_H = Math.max(...MAPS.map((m) => m.courtH));
/** Superficie de una cancha respecto a la más grande, en porcentaje redondo. */
function relativeArea(m: MapConfig): string {
  const pct = Math.round(((m.courtW * m.courtH) / (MAX_COURT_W * MAX_COURT_H)) * 100);
  return `${pct}% de la cancha más grande`;
}

/**
 * Vista previa de una cancha, **a escala comparable entre mapas**.
 *
 * El lienzo siempre mide lo mismo (el de la cancha mayor) y cada cancha se
 * dibuja dentro con el mismo factor de escala, centrada. Así una cancha más
 * pequeña SE VE más pequeña, que es justo lo que se está eligiendo. Antes cada
 * preview se estiraba hasta llenar su caja (`preserveAspectRatio="none"`), y
 * además deformaba la proporción del campo.
 */
function mapPreviewSVG(m: MapConfig, height = 210): string {
  const pad = 14;
  const W = MAX_COURT_W, H = MAX_COURT_H; // sistema de coordenadas del mundo
  const x = (W - m.courtW) / 2;
  const y = (H - m.courtH) / 2;
  const w = m.courtW;
  const h = m.courtH;
  const gh = m.goalH;
  const gy = y + (h - gh) / 2;
  const rr = 26;
  const post = 26;
  const boxH = Math.min(h * 0.62, gh + 180);
  const boxW = 150;
  return `<svg viewBox="${-pad} ${-pad} ${W + pad * 2} ${H + pad * 2}"
      preserveAspectRatio="xMidYMid meet"
      style="width:100%;height:${height}px;display:block;">
    <rect x="${x}" y="${y}" width="${w}" height="${h}" rx="${rr}" fill="rgba(34,230,255,0.05)" stroke="rgba(34,230,255,0.42)" stroke-width="6"/>
    <line x1="${x + w / 2}" y1="${y}" x2="${x + w / 2}" y2="${y + h}" stroke="rgba(34,230,255,0.3)" stroke-width="5"/>
    <circle cx="${x + w / 2}" cy="${y + h / 2}" r="${Math.min(w, h) * 0.11}" fill="none" stroke="rgba(34,230,255,0.3)" stroke-width="5"/>
    <rect x="${x}" y="${y + (h - boxH) / 2}" width="${boxW}" height="${boxH}" fill="none" stroke="rgba(34,230,255,0.2)" stroke-width="4"/>
    <rect x="${x + w - boxW}" y="${y + (h - boxH) / 2}" width="${boxW}" height="${boxH}" fill="none" stroke="rgba(34,230,255,0.2)" stroke-width="4"/>
    <rect x="${x - post}" y="${gy}" width="${post}" height="${gh}" rx="8" fill="#ff2e63"/>
    <rect x="${x + w}" y="${gy}" width="${post}" height="${gh}" rx="8" fill="#22e6ff"/>
  </svg>`;
}
